import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { proposalsApi, catalogApi, scheduleApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import type { Proposal, Service, ProposalFormData, ProposalStatus, ProposalItem, Vehicle } from '../../types';
import ObligationsStep from './Steps/ObligationsStep';
import ItemSelectionChoiceModal from '../../components/ItemSelectionChoiceModal';
import ItemSelectionModal from '../../components/ItemSelectionModal';
import { 
  PlusIcon, 
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

interface NegotiationHistory {
  id: string;
  timestamp: string;
  type: 'interaction' | 'item_change' | 'price_change' | 'status_change';
  description: string;
  user_name: string;
  details?: any;
}

interface NegotiationItem {
  id: string;
  service_id: string;
  service_name: string;
  original_quantity: number;
  original_price: number;
  current_quantity: number;
  current_price: number;
  pricing_period?: 'daily' | 'weekly' | 'monthly' | 'fixed';
  notes?: string;

  // Configuração de frota (veículos)
  is_vehicle?: boolean;
  fleet_km_included?: number;
  fleet_hours_included?: number;
  fleet_driver_included?: boolean;
  fleet_helper_included?: boolean;
  fleet_calculated_price?: number;
  fleet_config_notes?: string;

  // Valores padrão do serviço (para referência)
  default_km_included?: number;
  default_hours_included?: number;
  price_per_km?: number;
  price_per_hour?: number;
  price_driver_daily?: number;
  price_helper_daily?: number;

  // Logística individual por item
  individual_start_date?: string;
  individual_end_date?: string;
  custom_delivery_address?: string;
  individual_zip_code?: string;
  individual_street?: string;
  individual_number?: string;
  individual_complement?: string;
  individual_neighborhood?: string;
  individual_city?: string;
  individual_state?: string;
  individual_latitude?: number;
  individual_longitude?: number;

  // Campos calculados de distância
  calculated_distance_km?: number;
  calculated_duration_minutes?: number;
  route_polyline?: string;
  distance_calculated_at?: string;

  // NOVO: Campos de veículo e custos logísticos
  selected_vehicle?: string;
  is_round_trip?: boolean;
  calculated_logistics_cost?: number;
  calculated_toll_cost?: number;
  calculated_total_delivery?: number;
  toll_details?: any;

  // Recorrência
  service_days?: number[];
  monthly_breakdown?: Array<{
    month: string;
    month_name: string;
    days_count: { [key: string]: number };
    total_days: number;
    executions: number;
    value: number;
  }>;
  recurrence_occurrences?: number; // Número de ocorrências (apenas para exibição)

  service: Service;
}

const NegotiateProposal: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const proposalId = searchParams.get('id');
  
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [negotiationItems, setNegotiationItems] = useState<NegotiationItem[]>([]);
  const [negotiationHistory, setNegotiationHistory] = useState<NegotiationHistory[]>([]);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);

  // Estados para nova interação
  const [newInteraction, setNewInteraction] = useState('');
  const [showAddService, setShowAddService] = useState(false);
  const [searchService, setSearchService] = useState('');

  // Estados para desconto
  const [discountPercentage, setDiscountPercentage] = useState(0);

  // Estado para modal de histórico
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Estado para modal de interação
  const [showInteractionModal, setShowInteractionModal] = useState(false);

  // Estado para status da proposta
  const [proposalStatus, setProposalStatus] = useState<ProposalStatus>('novo_lead');

  // Estados para datas editáveis
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rentalDays, setRentalDays] = useState(0);

  // Estado para breakdown de recorrência com surcharge
  const [recurrenceBreakdowns, setRecurrenceBreakdowns] = useState<Record<string, any>>({});
  
  // Estado para obrigações
  const [formData, setFormData] = useState<ProposalFormData>({
    client: '',
    responsible: '',
    start_date: '',
    end_date: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip_code: '',
    distance_km: 0,
    discount_percentage: 0,
    internal_notes: '',
    client_notes: '',
    contractor_obligations: [],
    client_obligations: [],
    general_observations: [],
    items: []
  });

  // Estados para modais de seleção de itens
  const [showItemSelectionChoice, setShowItemSelectionChoice] = useState(false);
  const [pendingApprovalData, setPendingApprovalData] = useState<any>(null);
  const [selectedProposalItem, setSelectedProposalItem] = useState<ProposalItem | null>(null);
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState(false);
  const [showDatesWarningModal, setShowDatesWarningModal] = useState(false);

  // Estado para controlar se é proposta histórica (permite datas passadas)
  const [isHistoricalProposal, setIsHistoricalProposal] = useState(false);

  // Data mínima para propostas normais (hoje)
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  };

  useEffect(() => {
    console.log('🔥 NEGOTIATE - ID da proposta:', proposalId, typeof proposalId);
    if (proposalId && proposalId !== 'undefined' && proposalId.trim() !== '') {
      // Mais tolerante: pelo menos tem formato UUID básico
      if (proposalId.length >= 30 && proposalId.includes('-')) {
        console.log('✅ NegotiateProposal: Loading proposal with ID:', proposalId);
        loadProposal();
        loadServices();
      } else {
        console.log('ℹ️ NegotiateProposal: ID formato inválido:', proposalId, 'length:', proposalId.length);
        setError('ID da proposta inválido - ' + proposalId);
      }
    } else {
      console.log('ℹ️ NegotiateProposal: ID não fornecido:', proposalId);
      setError('ID da proposta não fornecido');
    }
  }, [proposalId]);

  // Carregar lista de veículos disponíveis
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        setLoadingVehicles(true);
        const response = await catalogApi.getVehicles({ is_active: true });
        setVehicles(response.results || []);
      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
      } finally {
        setLoadingVehicles(false);
      }
    };

    loadVehicles();
  }, []);

  // NOVO: Buscar breakdown automaticamente quando items com recorrência são carregados
  useEffect(() => {
    if (negotiationItems.length > 0 && startDate && endDate) {
      console.log('🎯 useEffect: Detectou items com datas, buscando breakdowns...');
      negotiationItems.forEach(item => {
        if (item.service_days && item.service_days.length > 0 && !recurrenceBreakdowns[item.id]) {
          console.log('🚀 useEffect: Buscando breakdown para:', item.service_name);
          fetchRecurrenceBreakdown(item.id, item);
        }
      });
    }
  }, [negotiationItems, startDate, endDate]);

  const loadProposal = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await proposalsApi.getProposal(proposalId!);
      setProposal(response);

      // Verificar se é proposta histórica (permite datas passadas)
      setIsHistoricalProposal(response.is_historical || false);
      console.log('📜 Proposta histórica?', response.is_historical ? 'SIM - permite datas passadas' : 'NÃO - apenas futuro');

      // Load negotiation history after proposal is set
      console.log('🔍 Proposta carregada:', response.number, 'Histórico encontrado:', response?.history?.length || 0, 'registros');
      
      if (response?.history) {
        console.log('📜 Histórico bruto do backend:', response.history);
        
        const convertedHistory: NegotiationHistory[] = response.history.map((historyItem) => {
          let type: 'interaction' | 'item_change' | 'price_change' | 'status_change' = 'interaction';
          if (historyItem.action === 'NEGOCIACAO_ATUALIZADA' && historyItem.old_status && historyItem.new_status) {
            type = 'status_change';
          } else if (historyItem.action === 'NEGOCIACAO_ATUALIZADA') {
            type = 'item_change';
          }

          return {
            id: historyItem.id,
            timestamp: historyItem.created_at,
            type,
            description: historyItem.description,
            user_name: historyItem.user_name || 'Usuário',
            details: {
              action: historyItem.action,
              old_status: historyItem.old_status,
              new_status: historyItem.new_status
            }
          };
        });
        
        console.log('📝 Histórico convertido para frontend:', convertedHistory);
        setNegotiationHistory(convertedHistory);
      } else {
        console.log('⚠️ Nenhum histórico encontrado na resposta da API');
        setNegotiationHistory([]);
      }

      // Converter itens da proposta para itens de negociação
      const items: NegotiationItem[] = response.items?.map((item) => {
        const service = typeof item.service === 'object' ? item.service : null;
        const isVehicle = service?.category_is_vehicle || false;

        // Calcular quantidade e preço baseado em recorrência se existir
        let calculatedQuantity = item.quantity || 1;
        let calculatedPrice = parseFloat(item.unit_price?.toString() || '0');

        let recurrenceOccurrences: number | undefined = undefined;

        // Se tem recorrência configurada, usar dados do monthly_breakdown
        if (item.service_days && item.service_days.length > 0 && item.monthly_breakdown) {
          // CORREÇÃO: Quantidade representa número de locais (ex: banheiros), NÃO dias
          // - current_quantity = item.quantity (quantidade de banheiros)
          // - current_price = total_price / quantity (preço POR banheiro)
          // - Os dias de limpeza estão em monthly_breakdown
          calculatedQuantity = item.quantity || 1;
          calculatedPrice = parseFloat(item.total_price?.toString() || '0') / calculatedQuantity;

          console.log('📥 Carregando item com recorrência:', {
            service: item.service_name,
            quantity: calculatedQuantity,
            unit_price: calculatedPrice,
            total_price: parseFloat(item.total_price?.toString() || '0')
          });
        }

        const negotiationItem: NegotiationItem = {
          id: item.id || `temp-${Date.now()}`,
          service_id: service?.id?.toString() || String(item.service || ''),
          service_name: item.service_name || 'Equipamento sem nome',
          original_quantity: item.quantity || 1,
          original_price: parseFloat(item.unit_price?.toString() || '0'),
          current_quantity: calculatedQuantity,
          current_price: calculatedPrice,
          pricing_period: item.pricing_period || 'daily',
          notes: item.notes || '',

          // Configuração de frota (apenas para veículos)
          is_vehicle: isVehicle,
          fleet_km_included: item.fleet_km_included,
          fleet_hours_included: item.fleet_hours_included,
          fleet_driver_included: item.fleet_driver_included || false,
          fleet_helper_included: item.fleet_helper_included || false,
          fleet_calculated_price: item.fleet_calculated_price ? parseFloat(item.fleet_calculated_price.toString()) : undefined,
          fleet_config_notes: item.fleet_config_notes,

          // Logística individual
          individual_start_date: item.individual_start_date,
          individual_end_date: item.individual_end_date,
          custom_delivery_address: item.custom_delivery_address,
          individual_zip_code: item.individual_zip_code,
          individual_street: item.individual_street,
          individual_number: item.individual_number,
          individual_complement: item.individual_complement,
          individual_neighborhood: item.individual_neighborhood,
          individual_city: item.individual_city,
          individual_state: item.individual_state,
          individual_latitude: item.individual_latitude ? parseFloat(item.individual_latitude.toString()) : undefined,
          individual_longitude: item.individual_longitude ? parseFloat(item.individual_longitude.toString()) : undefined,

          // Campos calculados de distância
          calculated_distance_km: item.calculated_distance_km ? parseFloat(item.calculated_distance_km.toString()) : undefined,
          calculated_duration_minutes: item.calculated_duration_minutes ? parseInt(item.calculated_duration_minutes.toString()) : undefined,
          route_polyline: item.route_polyline,
          distance_calculated_at: item.distance_calculated_at,

          // NOVO: Campos de veículo e custos logísticos
          selected_vehicle: item.selected_vehicle,
          is_round_trip: item.is_round_trip || false,
          calculated_logistics_cost: item.calculated_logistics_cost ? parseFloat(item.calculated_logistics_cost.toString()) : undefined,
          calculated_toll_cost: item.calculated_toll_cost ? parseFloat(item.calculated_toll_cost.toString()) : undefined,
          calculated_total_delivery: item.calculated_total_delivery ? parseFloat(item.calculated_total_delivery.toString()) : undefined,
          toll_details: item.toll_details,

          // Recorrência
          service_days: item.service_days,
          monthly_breakdown: item.monthly_breakdown,

          // Objeto service completo
          service: service as Service,

          // Valores padrão do serviço (para referência no cálculo)
          default_km_included: service?.default_km_included,
          default_hours_included: service?.default_hours_included,
          price_per_km: service?.price_per_km ? parseFloat(service.price_per_km.toString()) : undefined,
          price_per_hour: service?.price_per_hour ? parseFloat(service.price_per_hour.toString()) : undefined,
          price_driver_daily: service?.price_driver_daily ? parseFloat(service.price_driver_daily.toString()) : undefined,
          price_helper_daily: service?.price_helper_daily ? parseFloat(service.price_helper_daily.toString()) : undefined,
        };

        // Se for veículo, recalcular o preço automaticamente ao carregar
        // Isso garante que o preço reflita o período de locação total e a configuração de frota
        if (isVehicle) {
          const calculated = calculateFleetPriceForItem(negotiationItem, response.rental_period_days || 1);
          // IMPORTANTE: NÃO sobrescrever original_price! Ele deve sempre ser o preço unitário DIÁRIO
          // Apenas atualizar current_price com o valor total calculado
          negotiationItem.current_price = calculated.price;
          negotiationItem.fleet_calculated_price = calculated.price;
          negotiationItem.fleet_config_notes = calculated.notes;
        }

        return negotiationItem;
      }) || [];

      setNegotiationItems(items);
      setDiscountPercentage(parseFloat(response.discount_percentage?.toString() || '0'));

      // Inicializar datas editáveis
      setStartDate(response.start_date);
      setEndDate(response.end_date);
      setRentalDays(response.rental_period_days);

      // Debug: Log do status recebido
      console.log('🔍 Status recebido da proposta:', response.status);
      console.log('🔍 Status que será definido:', response.status || 'em_negociacao');
      setProposalStatus(response.status || 'em_negociacao');
      
      // Atualizar formData com as obrigações da proposta
      // Converter strings do backend para arrays no frontend
      const convertStringToArray = (value: string | string[] | undefined): string[] => {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string' && value.trim()) {
          return value.split('\n').filter(item => item.trim());
        }
        return [];
      };

      setFormData(prev => ({
        ...prev,
        contractor_obligations: convertStringToArray(response.contractor_obligations),
        client_obligations: convertStringToArray(response.client_obligations),
        general_observations: convertStringToArray(response.general_observations)
      }));

      // Verificar se precisa mostrar alerta de datas (apenas para propostas novas sem datas)
      // Chave para localStorage específica por proposta
      const warningDismissedKey = `dates_warning_dismissed_${proposalId}`;
      const wasAlreadyDismissed = localStorage.getItem(warningDismissedKey) === 'true';

      // Verificar se tem datas globais
      const hasGlobalDates = response.start_date && response.end_date && response.rental_period_days;

      // Verificar se algum item tem datas individuais
      const hasItemsWithDates = response.items?.some((item: any) =>
        item.individual_start_date || item.individual_end_date || item.individual_rental_period_days
      );

      // Mostrar alerta apenas se:
      // 1. Não foi dismissado antes
      // 2. Não tem datas globais E não tem nenhum item com datas individuais
      const shouldShowWarning = !wasAlreadyDismissed && !hasGlobalDates && !hasItemsWithDates;

      if (shouldShowWarning) {
        // Mostrar modal após 1 segundo (para dar tempo de renderizar a página)
        setTimeout(() => {
          setShowDatesWarningModal(true);
        }, 1000);
      }

    } catch (err: any) {
      console.error('Erro ao carregar proposta:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao carregar proposta');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const params: any = { page_size: 100 };
      const response = await catalogApi.getServices(params);
      setAvailableServices(response.results || []);
    } catch (err) {
      console.error('Erro ao carregar equipamentos:', err);
    }
  };

  // Função para recalcular dias quando as datas mudam
  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    // Sempre atualizar as datas, mesmo se uma estiver vazia
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    // Só calcular os dias se AMBAS as datas estiverem preenchidas
    if (!newStartDate || !newEndDate) {
      setRentalDays(0);
      return;
    }

    const start = new Date(newStartDate);
    const end = new Date(newEndDate);

    if (end < start) {
      alert('⚠️ A data final não pode ser anterior à data inicial!');
      setRentalDays(0);
      return;
    }

    // Calcular número de dias (+1 porque inclui ambos os dias)
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    console.log('📅 Datas alteradas:', {
      start: newStartDate,
      end: newEndDate,
      days: diffDays
    });

    setRentalDays(diffDays);

    // Recalcular preços de todos os items com os novos dias
    setNegotiationItems(prev => prev.map(item => {
      if (item.is_vehicle) {
        // Veículos: recalcular preço de frota
        const calculated = calculateFleetPriceForItem(item, diffDays);
        return {
          ...item,
          current_price: calculated.price,
          fleet_calculated_price: calculated.price,
          fleet_config_notes: calculated.notes
        };
      } else {
        // Equipamentos normais: recalcular periodicidade e preço
        const service = availableServices.find(s => s.id === item.service_id);
        if (service) {
          const { price, period } = getBestPriceForPeriod(service, diffDays);
          // Só atualizar se a periodicidade mudou
          if (period !== item.pricing_period) {
            return {
              ...item,
              current_price: price,
              original_price: price, // Atualizar também o preço original
              pricing_period: period
            };
          }
        }
        return item;
      }
    }));
  };


  const addInteraction = () => {
    if (!newInteraction.trim()) return;

    const interaction: NegotiationHistory = {
      id: `interaction-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'interaction',
      description: newInteraction,
      user_name: user?.first_name + ' ' + user?.last_name || 'Usuário'
    };

    setNegotiationHistory(prev => [interaction, ...prev]);
    setNewInteraction('');
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    const item = negotiationItems.find(i => i.id === itemId);
    if (!item) return;

    // Verificar stock disponível para o equipamento
    const service = availableServices.find(s => s.id === item.service_id);
    if (service && quantity > service.available_quantity) {
      alert(`❌ Stock insuficiente para ${item.service_name}.\nDisponível: ${service.available_quantity}\nSolicitado: ${quantity}`);
      return;
    }

    setNegotiationItems(prev => prev.map(i =>
      i.id === itemId
        ? { ...i, current_quantity: Math.max(1, quantity) }
        : i
    ));

    // Adicionar ao histórico
    const historyEntry: NegotiationHistory = {
      id: `change-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'item_change',
      description: `Quantidade de "${item.service_name}" alterada para ${quantity}`,
      user_name: user?.first_name + ' ' + user?.last_name || 'Usuário',
      details: { item_id: itemId, field: 'quantity', from: item.current_quantity, to: quantity }
    };
    setNegotiationHistory(prev => [historyEntry, ...prev]);
  };

  const updateItemPrice = (itemId: string, price: number) => {
    setNegotiationItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, current_price: Math.max(0, price) }
        : item
    ));

    // Adicionar ao histórico
    const item = negotiationItems.find(i => i.id === itemId);
    if (item) {
      const historyEntry: NegotiationHistory = {
        id: `change-${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'price_change',
        description: `Preço de "${item.service_name}" alterado para R$ ${price.toFixed(2)}`,
        user_name: user?.first_name + ' ' + user?.last_name || 'Usuário',
        details: { item_id: itemId, field: 'price', from: item.current_price, to: price }
      };
      setNegotiationHistory(prev => [historyEntry, ...prev]);
    }
  };

  const removeItem = (itemId: string) => {
    const item = negotiationItems.find(i => i.id === itemId);
    if (!item) return;

    setNegotiationItems(prev => prev.filter(i => i.id !== itemId));

    // Adicionar ao histórico
    const historyEntry: NegotiationHistory = {
      id: `remove-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'item_change',
      description: `Item "${item.service_name}" removido da negociação`,
      user_name: user?.first_name + ' ' + user?.last_name || 'Usuário',
      details: { item_id: itemId, action: 'removed' }
    };
    setNegotiationHistory(prev => [historyEntry, ...prev]);
  };

  const updateItemDates = (itemId: string, startDate: string | undefined, endDate: string | undefined) => {
    setNegotiationItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;

      const updatedItem = { ...item, individual_start_date: startDate, individual_end_date: endDate };

      // Se tem recorrência configurada, recalcular quantidade com as novas datas
      if (item.service_days && item.service_days.length > 0) {
        const effectiveStart = startDate || item.individual_start_date;
        const effectiveEnd = endDate || item.individual_end_date;

        if (effectiveStart && effectiveEnd) {
          const occurrences = calculateOccurrences(effectiveStart, effectiveEnd, item.service_days);

          console.log('🔄 Recalculando por mudança de datas:', {
            service: item.service_name,
            old_dates: `${item.individual_start_date} a ${item.individual_end_date}`,
            new_dates: `${effectiveStart} a ${effectiveEnd}`,
            old_recurrence_occurrences: item.recurrence_occurrences,
            new_occurrences: occurrences
          });

          // Calcular subtotal para recorrência
          const subtotal = occurrences * parseFloat(String(item.original_price));

          // Atualizar com a nova lógica de recorrência
          updatedItem.current_quantity = 1; // Para recorrência, quantidade interna é sempre 1
          updatedItem.current_price = subtotal; // Preço = subtotal (ocorrências × preço unitário)
          updatedItem.recurrence_occurrences = occurrences; // IMPORTANTE: Atualizar este campo para exibir na tela
        }
      }

      return updatedItem;
    }));
  };

  const updateItemAddress = (itemId: string, address: string | undefined) => {
    setNegotiationItems(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, custom_delivery_address: address }
        : item
    ));
  };

  // Função para buscar CEP via ViaCEP
  const [loadingCep, setLoadingCep] = useState<string | null>(null);

  const fetchAddressByCep = async (itemId: string, cep: string) => {
    // Remover caracteres não numéricos
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      alert('CEP deve conter 8 dígitos');
      return;
    }

    setLoadingCep(itemId);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        alert('CEP não encontrado');
        setLoadingCep(null);
        return;
      }

      // Buscar coordenadas usando Google Geocoding API (mais preciso que Nominatim)
      const address = `${data.logradouro}, ${data.bairro}, ${data.localidade}, ${data.uf}, Brazil`;
      let latitude: number | undefined;
      let longitude: number | undefined;

      try {
        const googleApiKey = 'AIzaSyCINWVpkJ6cT8ecBccqtgmSzPW3qlwwimM';
        const geoResponse = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleApiKey}`
        );
        const geoData = await geoResponse.json();

        if (geoData.status === 'OK' && geoData.results && geoData.results.length > 0) {
          const location = geoData.results[0].geometry.location;
          latitude = location.lat;
          longitude = location.lng;

          console.log('✅ Geocodificação bem-sucedida:', {
            address,
            latitude,
            longitude
          });
        } else {
          console.warn('⚠️ Google Geocoding não encontrou coordenadas:', geoData.status);
        }
      } catch (geoError) {
        console.error('❌ Erro ao buscar coordenadas:', geoError);
        // Continua sem coordenadas
      }

      // Atualizar item com os dados do CEP
      setNegotiationItems(prev => prev.map(item =>
        item.id === itemId
          ? {
              ...item,
              individual_zip_code: cleanCep,
              individual_street: data.logradouro || '',
              individual_neighborhood: data.bairro || '',
              individual_city: data.localidade || '',
              individual_state: data.uf || '',
              individual_latitude: latitude,
              individual_longitude: longitude,
              custom_delivery_address: `${data.logradouro}, ${data.bairro}, ${data.localidade}/${data.uf}`
            }
          : item
      ));

      if (!latitude || !longitude) {
        alert('⚠️ Endereço encontrado, mas sem coordenadas geográficas.\nNão será possível calcular a distância automaticamente.');
      }

    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      alert('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCep(null);
    }
  };

  const updateIndividualAddressField = (itemId: string, field: string, value: any) => {
    setNegotiationItems(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, [field]: value }
        : item
    ));
  };

  // Função para extrair coordenadas de URL do Google Maps
  const [extractingFromUrl, setExtractingFromUrl] = useState<string | null>(null);

  const extractCoordinatesFromGoogleMapsUrl = async (itemId: string, url: string) => {
    setExtractingFromUrl(itemId);

    try {
      // Formatos suportados do Google Maps:
      // 1. @-23.3651675,-46.021123,17z
      // 2. !3d-23.3651675!4d-46.0185427
      // 3. ?q=-23.3651675,-46.021123
      // 4. /@-23.3651675,-46.021123

      let latitude: number | undefined;
      let longitude: number | undefined;

      // Formato 1: @lat,lng ou /@lat,lng
      const atPattern = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
      const atMatch = url.match(atPattern);
      if (atMatch) {
        latitude = parseFloat(atMatch[1]);
        longitude = parseFloat(atMatch[2]);
      }

      // Formato 2: !3dlat!4dlng (mais preciso)
      if (!latitude || !longitude) {
        const bangPattern = /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/;
        const bangMatch = url.match(bangPattern);
        if (bangMatch) {
          latitude = parseFloat(bangMatch[1]);
          longitude = parseFloat(bangMatch[2]);
        }
      }

      // Formato 3: ?q=lat,lng
      if (!latitude || !longitude) {
        const qPattern = /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/;
        const qMatch = url.match(qPattern);
        if (qMatch) {
          latitude = parseFloat(qMatch[1]);
          longitude = parseFloat(qMatch[2]);
        }
      }

      if (latitude && longitude) {
        // Fazer geocoding reverso para obter endereço completo
        const googleApiKey = 'AIzaSyCINWVpkJ6cT8ecBccqtgmSzPW3qlwwimM';
        const reverseGeoResponse = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${googleApiKey}`
        );
        const reverseGeoData = await reverseGeoResponse.json();

        let addressData: any = {};

        if (reverseGeoData.status === 'OK' && reverseGeoData.results && reverseGeoData.results.length > 0) {
          const result = reverseGeoData.results[0];
          const components = result.address_components;

          // Extrair componentes do endereço
          for (const component of components) {
            if (component.types.includes('route')) {
              addressData.street = component.long_name;
            }
            if (component.types.includes('street_number')) {
              addressData.number = component.long_name;
            }
            if (component.types.includes('sublocality') || component.types.includes('neighborhood')) {
              addressData.neighborhood = component.long_name;
            }
            if (component.types.includes('administrative_area_level_2')) {
              addressData.city = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              addressData.state = component.short_name;
            }
            if (component.types.includes('postal_code')) {
              addressData.zip_code = component.long_name;
            }
          }

          console.log('✅ Geocoding reverso bem-sucedido:', addressData);
        }

        // Atualizar item com coordenadas E endereço (se encontrado)
        setNegotiationItems(prev => prev.map(item =>
          item.id === itemId
            ? {
                ...item,
                individual_latitude: latitude,
                individual_longitude: longitude,
                individual_street: addressData.street || item.individual_street,
                individual_number: addressData.number || item.individual_number,
                individual_neighborhood: addressData.neighborhood || item.individual_neighborhood,
                individual_city: addressData.city || item.individual_city,
                individual_state: addressData.state || item.individual_state,
                individual_zip_code: addressData.zip_code || item.individual_zip_code,
                custom_delivery_address: addressData.street
                  ? `${addressData.street}${addressData.number ? ', ' + addressData.number : ''}, ${addressData.neighborhood || ''}, ${addressData.city || ''}/${addressData.state || ''}`
                  : item.custom_delivery_address
              }
            : item
        ));

        const message = addressData.street
          ? `✅ Coordenadas e endereço extraídos com sucesso!\n\n📍 ${addressData.street}${addressData.number ? ', ' + addressData.number : ''}\n${addressData.neighborhood || ''}\n${addressData.city || ''}/${addressData.state || ''}\n${addressData.zip_code || ''}\n\nLat: ${latitude.toFixed(6)}, Lng: ${longitude.toFixed(6)}`
          : `✅ Coordenadas extraídas com sucesso!\n\nLatitude: ${latitude.toFixed(6)}\nLongitude: ${longitude.toFixed(6)}`;

        alert(message);
      } else {
        alert('❌ Não foi possível extrair coordenadas desta URL.\n\nVerifique se é uma URL válida do Google Maps.\n\nExemplo: https://www.google.com/maps/@-23.3651675,-46.021123,17z');
      }
    } catch (error) {
      console.error('Erro ao extrair coordenadas:', error);
      alert('Erro ao processar a URL. Tente novamente.');
    } finally {
      setExtractingFromUrl(null);
    }
  };

  // Função para atualizar coordenadas baseado no endereço completo
  const [updatingCoordinates, setUpdatingCoordinates] = useState<string | null>(null);

  const updateCoordinatesFromAddress = async (itemId: string) => {
    const item = negotiationItems.find(i => i.id === itemId);
    if (!item) return;

    // Validar se tem endereço mínimo
    if (!item.individual_city || !item.individual_state) {
      alert('Configure pelo menos Cidade e Estado para atualizar as coordenadas');
      return;
    }

    setUpdatingCoordinates(itemId);

    try {
      // Montar endereço completo
      const addressParts = [];
      if (item.individual_street) addressParts.push(item.individual_street);
      if (item.individual_number) addressParts.push(item.individual_number);
      if (item.individual_neighborhood) addressParts.push(item.individual_neighborhood);
      if (item.individual_city) addressParts.push(item.individual_city);
      if (item.individual_state) addressParts.push(item.individual_state);

      const address = addressParts.join(', ') + ', Brazil';

      // Buscar coordenadas usando Google Geocoding API (mais preciso que Nominatim)
      const googleApiKey = 'AIzaSyCINWVpkJ6cT8ecBccqtgmSzPW3qlwwimM';
      const geoResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleApiKey}`
      );
      const geoData = await geoResponse.json();

      if (geoData.status === 'OK' && geoData.results && geoData.results.length > 0) {
        const location = geoData.results[0].geometry.location;
        const latitude = location.lat;
        const longitude = location.lng;
        const formattedAddress = geoData.results[0].formatted_address;

        // Atualizar coordenadas
        setNegotiationItems(prev => prev.map(i =>
          i.id === itemId
            ? {
                ...i,
                individual_latitude: latitude,
                individual_longitude: longitude
              }
            : i
        ));

        console.log('✅ Coordenadas atualizadas:', {
          address,
          formattedAddress,
          latitude,
          longitude
        });

        alert(`✅ Coordenadas atualizadas com sucesso!\n\n📍 ${formattedAddress}\nLat: ${latitude.toFixed(6)}, Lng: ${longitude.toFixed(6)}`);
      } else {
        console.error('❌ Google Geocoding falhou:', geoData.status, geoData.error_message);
        alert(`❌ Não foi possível encontrar coordenadas para este endereço.\nVerifique se os dados estão corretos.\n\nEndereço buscado: ${address}`);
      }
    } catch (error) {
      console.error('Erro ao buscar coordenadas:', error);
      alert('Erro ao buscar coordenadas. Tente novamente.');
    } finally {
      setUpdatingCoordinates(null);
    }
  };

  // Função para calcular distância via Google Directions API
  const [calculatingDistance, setCalculatingDistance] = useState<string | null>(null);

  // Estado para veículos (Services da categoria Mobilizados)
  const [vehicles, setVehicles] = useState<Service[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const calculateDistance = async (itemId: string) => {
    const item = negotiationItems.find(i => i.id === itemId);

    if (!item) return;

    // Verificar se tem coordenadas OU endereço
    const hasCoordinates = item.individual_latitude && item.individual_longitude;
    const hasAddress = item.individual_city && item.individual_state;

    if (!hasCoordinates && !hasAddress) {
      alert('Configure as coordenadas (latitude/longitude) OU um endereço completo (pelo menos cidade e estado) para calcular a distância');
      return;
    }

    setCalculatingDistance(itemId);

    try {
      // NOVA ABORDAGEM: Usar preview que não salva no banco
      const data = await proposalsApi.calculateDistancePreview({
        service_id: parseInt(item.service_id),
        latitude: item.individual_latitude || undefined,
        longitude: item.individual_longitude || undefined,
        address: item.custom_delivery_address || undefined,
        vehicle_id: item.selected_vehicle ? parseInt(item.selected_vehicle) : undefined,
        is_round_trip: item.is_round_trip || false
      });

      // Atualizar item com os dados calculados (apenas no estado local, não salva)
      setNegotiationItems(prev => prev.map(i =>
        i.id === itemId
          ? {
              ...i,
              // Se foi geocodificado, atualizar as coordenadas também
              individual_latitude: data.geocoded ? data.destination.latitude : i.individual_latitude,
              individual_longitude: data.geocoded ? data.destination.longitude : i.individual_longitude,
              // Dados de distância - IMPORTANTE: Arredondar para 2 casas decimais
              calculated_distance_km: parseFloat(data.distance_km.toFixed(2)),
              calculated_duration_minutes: data.duration_minutes,
              route_polyline: (data as any).polyline || '',
              distance_calculated_at: new Date().toISOString(),
              // NOVO: Dados de custos logísticos (se veículo foi selecionado)
              // IMPORTANTE: Arredondar para 2 casas decimais para evitar erro de validação
              calculated_logistics_cost: data.costs?.logistics_cost ? parseFloat(data.costs.logistics_cost.toFixed(2)) : undefined,
              calculated_toll_cost: data.costs?.toll_cost ? parseFloat(data.costs.toll_cost.toFixed(2)) : undefined,
              calculated_total_delivery: data.costs?.total_delivery_cost ? parseFloat(data.costs.total_delivery_cost.toFixed(2)) : undefined,
              toll_details: data.toll_details || undefined,
            }
          : i
      ));

      // Montar mensagem de sucesso
      let successMsg = `✅ Distância e custos calculados!${data.geocoded ? '\n🗺️ Endereço geocodificado automaticamente' : ''}\n\n` +
            `📍 Origem: ${data.origin.warehouse}\n` +
            `📍 Destino: ${data.destination.address}\n` +
            (data.geocoded ? `   Lat: ${data.destination.latitude.toFixed(6)}, Lng: ${data.destination.longitude.toFixed(6)}\n` : '') +
            `\n📏 Distância: ${data.distance_km.toFixed(2)} km${data.is_round_trip ? ' (ida/volta: ' + (data.distance_km * 2).toFixed(2) + ' km)' : ''}\n` +
            `⏱️ Tempo estimado: ${data.duration_text}`;

      // Se houver custos calculados, adicionar ao alerta
      if (data.costs) {
        successMsg += `\n\n💰 CUSTOS CALCULADOS:\n` +
              `⛽ Rodagem: R$ ${data.costs.logistics_cost.toFixed(2)}\n` +
              `🎫 Pedágios: R$ ${data.costs.toll_cost.toFixed(2)}\n` +
              `━━━━━━━━━━━━━━━━━━\n` +
              `💵 TOTAL: R$ ${data.costs.total_delivery_cost.toFixed(2)}\n\n` +
              `💡 Estes valores foram calculados e salvos localmente.\n` +
              `   Clique em "Salvar Proposta" para persistir no banco.`;
      }

      alert(successMsg);

    } catch (error: any) {
      console.error('Erro ao calcular distância:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao calcular distância. Tente novamente.';
      alert(`❌ ${errorMsg}`);
    } finally {
      setCalculatingDistance(null);
    }
  };

  const updateItemRecurrence = (itemId: string, serviceDays: number[] | undefined) => {
    console.log('🔄 updateItemRecurrence chamada:', { itemId, serviceDays });
    setNegotiationItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;

      // Se removeu a recorrência, restaurar valores normais
      if (!serviceDays || serviceDays.length === 0) {
        return {
          ...item,
          service_days: undefined,
          monthly_breakdown: undefined,
          current_quantity: 1,
          current_price: item.original_price,
          recurrence_occurrences: undefined // Limpar ocorrências
        };
      }

      // Recalcular quantidade baseado nos novos dias selecionados
      const effectiveStart = item.individual_start_date || startDate;
      const effectiveEnd = item.individual_end_date || endDate;

      let occurrences = 1; // padrão se não tiver datas
      if (effectiveStart && effectiveEnd) {
        occurrences = calculateOccurrences(effectiveStart, effectiveEnd, serviceDays);

        console.log('🔄 Recalculando por mudança de dias:', {
          service: item.service_name,
          dates: `${effectiveStart} a ${effectiveEnd}`,
          old_days: item.service_days,
          new_days: serviceDays,
          old_quantity: item.current_quantity,
          old_recurrence_occurrences: item.recurrence_occurrences,
          new_occurrences: occurrences
        });
      }

      // Calcular subtotal para recorrência
      const subtotal = occurrences * parseFloat(String(item.original_price));

      const updatedItem = {
        ...item,
        service_days: serviceDays,
        current_quantity: 1, // Para recorrência, quantidade interna é sempre 1
        current_price: subtotal, // Preço = subtotal (ocorrências × preço unitário)
        recurrence_occurrences: occurrences, // IMPORTANTE: Atualizar este campo para exibir na tela
        // Limpar monthly_breakdown pois mudamos os dias
        monthly_breakdown: undefined
      };

      // Buscar breakdown com surcharge (assíncrono, não bloqueia UI)
      setTimeout(() => fetchRecurrenceBreakdown(itemId, updatedItem), 100);

      return updatedItem;
    }));
  };

  const calculateOccurrences = (startDate: string, endDate: string, serviceDays: number[]): number => {
    if (!startDate || !endDate || !serviceDays || serviceDays.length === 0) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;

    const current = new Date(start);
    while (current <= end) {
      const weekday = current.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
      // Converter: domingo (0) -> 6, segunda (1) -> 0, ..., sábado (6) -> 5
      const adjustedWeekday = weekday === 0 ? 6 : weekday - 1;

      if (serviceDays.includes(adjustedWeekday)) {
        count++;
      }

      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  // Função para buscar breakdown de recorrência com surcharge (fins de semana/feriados)
  const fetchRecurrenceBreakdown = async (itemId: string, item: NegotiationItem) => {
    const effectiveStart = item.individual_start_date || startDate;
    const effectiveEnd = item.individual_end_date || endDate;

    console.log('🔍 fetchRecurrenceBreakdown chamada:', {
      itemId,
      service: item.service_name,
      effectiveStart,
      effectiveEnd,
      service_days: item.service_days,
      service_id: item.service?.id || item.service_id
    });

    if (!effectiveStart || !effectiveEnd || !item.service_days || item.service_days.length === 0) {
      console.warn('❌ Faltam dados para calcular breakdown');
      return;
    }

    // Verificar se service_id existe
    const serviceId = item.service?.id || item.service_id;
    if (!serviceId) {
      console.warn('❌ Item sem service_id, pulando cálculo de recorrência');
      return;
    }

    try {
      // Pegar token de autenticação
      const tokenData = localStorage.getItem('authTokens');
      const tokens = tokenData ? JSON.parse(tokenData) : null;
      const accessToken = tokens?.access;

      if (!accessToken) {
        console.warn('❌ Token de autenticação não encontrado');
        return;
      }

      const requestBody = {
        start_date: effectiveStart,
        end_date: effectiveEnd,
        service_days: item.service_days,
        service_id: item.service?.id || item.service_id,
        estado: item.individual_state || 'SP',
        cidade: item.individual_city || undefined
      };

      console.log('📤 Enviando request para API:', requestBody);

      // Construir URL da API de forma inteligente
      const currentHost = window.location.hostname;
      const protocol = window.location.protocol;
      const isDomain = currentHost.includes('.') && !currentHost.match(/^\d+\.\d+\.\d+\.\d+$/);
      const apiBaseUrl = isDomain ? `${protocol}//${currentHost}` : `${protocol}//${currentHost}:8000`;

      const response = await fetch(`${apiBaseUrl}/api/v1/proposal-items/calculate-recurrence/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const breakdown = await response.json();
        console.log('✅ Breakdown recebido da API:', breakdown);
        setRecurrenceBreakdowns(prev => {
          const newState = {
            ...prev,
            [itemId]: breakdown
          };
          console.log('✅ Estado recurrenceBreakdowns atualizado:', newState);
          return newState;
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Erro ao calcular recorrência:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar breakdown de recorrência:', error);
    }
  };

  // Função para determinar o melhor preço baseado no período de locação
  // NOVA LÓGICA: Calcula preço proporcional inteligente (nunca cobra menos que uma unidade completa)
  const getBestPriceForPeriod = (service: Service, days: number): { price: number; period: 'daily' | 'weekly' | 'monthly' | 'fixed' } => {
    // ⭐ NOVA LÓGICA PARA SERVIÇOS:
    // Se for um SERVIÇO (item_type='service'), usar base_rate e pricing_model
    if (service.item_type === 'service' && service.base_rate) {
      const baseRate = parseFloat(String(service.base_rate));

      // Se for preço fixo, retornar direto sem período
      if (service.pricing_model === 'fixed') {
        return { price: baseRate, period: 'fixed' };
      }

      // Para outros modelos de serviço (hourly, per_person, recurring, per_unit)
      // usar o base_rate como preço diário por padrão
      return { price: baseRate, period: 'daily' };
    }

    // ⭐ LÓGICA ORIGINAL PARA PRODUTOS:
    const dailyPrice = service.daily_price ? parseFloat(String(service.daily_price)) : 0;
    const weeklyPrice = service.weekly_price ? parseFloat(String(service.weekly_price)) : 0;
    const monthlyPrice = service.monthly_price ? parseFloat(String(service.monthly_price)) : 0;

    // 1. Se tem preço diário, usar sempre (mais simples e direto)
    if (dailyPrice > 0) {
      return { price: dailyPrice, period: 'daily' };
    }

    // 2. Para períodos < 30 dias, tentar usar preço semanal
    if (days < 30 && weeklyPrice > 0) {
      return { price: weeklyPrice, period: 'weekly' };
    }

    // 3. Para períodos >= 30 dias, tentar usar preço mensal
    if (days >= 30 && monthlyPrice > 0) {
      return { price: monthlyPrice, period: 'monthly' };
    }

    // 4. Fallbacks: se não tem o preço ideal, usar o que tiver
    if (weeklyPrice > 0) {
      return { price: weeklyPrice, period: 'weekly' };
    }

    if (monthlyPrice > 0) {
      return { price: monthlyPrice, period: 'monthly' };
    }

    // 5. Último recurso: não deveria chegar aqui (produto sem preço)
    return { price: 0, period: 'daily' };
  };

  const addService = (service: Service) => {
    // Verificar se o equipamento tem stock disponível
    if (service.available_quantity <= 0) {
      alert(`❌ ${service.name} não possui stock disponível (${service.available_quantity}/${service.total_quantity})`);
      return;
    }

    const isVehicle = service.category_is_vehicle || false;

    // Determinar pricing_period e unit_price baseado no período de locação
    const days = rentalDays || 1;
    const { price, period } = getBestPriceForPeriod(service, days);

    const newItem: NegotiationItem = {
      id: `new-${Date.now()}`,
      service_id: service.id,
      service_name: service.name,
      original_quantity: 0, // Novo item
      original_price: price,
      current_quantity: 1,
      current_price: price,
      pricing_period: period,
      notes: 'Item adicionado durante negociação',

      // Objeto service completo
      service: service,

      // Configuração de frota (apenas para veículos)
      is_vehicle: isVehicle,
      fleet_km_included: service.default_km_included,
      fleet_hours_included: service.default_hours_included,
      fleet_driver_included: false,
      fleet_helper_included: false,

      // Valores padrão do serviço (converter para number ou undefined)
      default_km_included: service.default_km_included,
      default_hours_included: service.default_hours_included,
      price_per_km: service.price_per_km ? parseFloat(service.price_per_km.toString()) : undefined,
      price_per_hour: service.price_per_hour ? parseFloat(service.price_per_hour.toString()) : undefined,
      price_driver_daily: service.price_driver_daily ? parseFloat(service.price_driver_daily.toString()) : undefined,
      price_helper_daily: service.price_helper_daily ? parseFloat(service.price_helper_daily.toString()) : undefined,
    };

    setNegotiationItems(prev => [...prev, newItem]);
    setShowAddService(false);
    setSearchService('');

    // Adicionar ao histórico
    const historyEntry: NegotiationHistory = {
      id: `add-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'item_change',
      description: `Novo item "${service.name}" adicionado à negociação`,
      user_name: user?.first_name + ' ' + user?.last_name || 'Usuário',
      details: { service_id: service.id, action: 'added' }
    };
    setNegotiationHistory(prev => [historyEntry, ...prev]);
  };

  // Funções para atualizar configuração de frota
  const updateFleetConfig = (itemId: string, field: string, value: any) => {
    setNegotiationItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;

      const updatedItem = { ...item, [field]: value };

      // Se for um veículo, recalcular o preço automaticamente
      if (updatedItem.is_vehicle) {
        const calculated = calculateFleetPrice(updatedItem);
        updatedItem.fleet_calculated_price = calculated.price;
        updatedItem.fleet_config_notes = calculated.notes;
        // Atualizar o preço atual com o calculado (pode ser editado manualmente depois)
        updatedItem.current_price = calculated.price;
      }

      return updatedItem;
    }));
  };

  // Helper function to calculate fleet price (can be called with or without proposal state)
  const calculateFleetPriceForItem = (item: NegotiationItem, rentalDays: number) => {
    if (!item.is_vehicle) {
      return { price: item.current_price, notes: '' };
    }

    // Garantir que todos os valores sejam números
    const basePrice = Number(item.original_price) || 0;
    const pricePerKm = Number(item.price_per_km) || 0;
    const pricePerHour = Number(item.price_per_hour) || 0;
    const priceDriverDaily = Number(item.price_driver_daily) || 0;
    const priceHelperDaily = Number(item.price_helper_daily) || 0;

    let totalPrice = basePrice * rentalDays;
    const notes: string[] = [];

    console.log('🔍 Calculando preço de frota:', {
      item_name: item.service_name,
      rental_days: rentalDays,
      base_price: basePrice,
      fleet_km_included: item.fleet_km_included,
      default_km_included: item.default_km_included,
      price_per_km: pricePerKm,
      fleet_hours_included: item.fleet_hours_included,
      default_hours_included: item.default_hours_included,
      price_per_hour: pricePerHour
    });

    notes.push(`Diária base: R$ ${basePrice.toFixed(2)} x ${rentalDays} dias = R$ ${(basePrice * rentalDays).toFixed(2)}`);

    // Calcular KM adicionais
    if (item.fleet_km_included && item.default_km_included && pricePerKm > 0) {
      const extraKm = Math.max(0, item.fleet_km_included - item.default_km_included);
      console.log('  📊 KM:', { fleet_km: item.fleet_km_included, default_km: item.default_km_included, extra_km: extraKm });
      if (extraKm > 0) {
        const kmCost = extraKm * pricePerKm * rentalDays;
        totalPrice += kmCost;
        notes.push(`KM extras: ${extraKm} km/dia x R$ ${pricePerKm.toFixed(2)} x ${rentalDays} dias = R$ ${kmCost.toFixed(2)}`);
        console.log('  💰 Custo KM extras:', kmCost);
      }
    }

    // Calcular horas adicionais
    if (item.fleet_hours_included && item.default_hours_included && pricePerHour > 0) {
      const extraHours = Math.max(0, item.fleet_hours_included - item.default_hours_included);
      if (extraHours > 0) {
        const hoursCost = extraHours * pricePerHour * rentalDays;
        totalPrice += hoursCost;
        notes.push(`Horas extras: ${extraHours} h/dia x R$ ${pricePerHour.toFixed(2)} x ${rentalDays} dias = R$ ${hoursCost.toFixed(2)}`);
      }
    }

    // Adicionar motorista
    if (item.fleet_driver_included && priceDriverDaily > 0) {
      const driverCost = priceDriverDaily * rentalDays;
      totalPrice += driverCost;
      notes.push(`Motorista: R$ ${priceDriverDaily.toFixed(2)} x ${rentalDays} dias = R$ ${driverCost.toFixed(2)}`);
    }

    // Adicionar ajudante
    if (item.fleet_helper_included && priceHelperDaily > 0) {
      const helperCost = priceHelperDaily * rentalDays;
      totalPrice += helperCost;
      notes.push(`Ajudante: R$ ${priceHelperDaily.toFixed(2)} x ${rentalDays} dias = R$ ${helperCost.toFixed(2)}`);
    }

    notes.push(`\nTOTAL CALCULADO: R$ ${totalPrice.toFixed(2)}`);

    return {
      price: totalPrice,
      notes: notes.join('\n')
    };
  };

  const calculateFleetPrice = (item: NegotiationItem) => {
    // Usar rentalDays do estado em vez de proposal.rental_period_days
    return calculateFleetPriceForItem(item, rentalDays || 1);
  };

  // Função para calcular preço proporcional de um item baseado na periodicidade
  // LÓGICA: Sempre cobra pelo menos 1 unidade completa, depois proporcional
  const calculateProportionalPrice = (unitPrice: number, pricingPeriod: string | undefined, days: number): number => {
    if (!pricingPeriod || pricingPeriod === 'daily') {
      // Diário: simples multiplicação
      return unitPrice * days;
    } else if (pricingPeriod === 'fixed') {
      // Preço fixo: não multiplica
      return unitPrice;
    } else if (pricingPeriod === 'weekly') {
      // Semanal: sempre cobra pelo menos 1 semana
      const fullWeeks = Math.max(1, Math.floor(days / 7));
      const remainingDays = days > 7 ? days % 7 : 0;
      let total = unitPrice * fullWeeks;

      // Só adiciona proporcional se tiver dias EXTRAS (mais de 1 semana)
      if (remainingDays > 0 && days > 7) {
        const dailyRate = unitPrice / 7.0;
        total += dailyRate * remainingDays;
      }

      return total;
    } else if (pricingPeriod === 'monthly') {
      // Mensal: sempre cobra pelo menos 1 mês
      const fullMonths = Math.max(1, Math.floor(days / 30));
      const remainingDays = days > 30 ? days % 30 : 0;
      let total = unitPrice * fullMonths;

      // Só adiciona proporcional se tiver dias EXTRAS (mais de 1 mês)
      if (remainingDays > 0 && days > 30) {
        const dailyRate = unitPrice / 30.0;
        total += dailyRate * remainingDays;
      }

      return total;
    }

    // Fallback: multiplicação simples
    return unitPrice * days;
  };

  const calculateOriginalTotal = () => {
    return negotiationItems.reduce((total, item) => {
      // TIPO 3: Serviço recorrente (service_days existe)
      const breakdown = recurrenceBreakdowns[item.id];
      if (item.service_days && item.service_days.length > 0 && breakdown?.total_price) {
        return total + (item.original_quantity * breakdown.total_price);
      }
      // TIPO 2: Veículo de frota
      else if (item.is_vehicle && item.fleet_calculated_price) {
        return total + (item.original_quantity * item.fleet_calculated_price);
      }
      // TIPO 1: Equipamentos com aluguel (pricing_period)
      else {
        // Equipamentos normais: usar cálculo proporcional
        const priceForPeriod = calculateProportionalPrice(
          item.original_price,
          item.pricing_period,
          rentalDays || 1
        );
        return total + (item.original_quantity * priceForPeriod);
      }
    }, 0);
  };

  const calculateCurrentTotal = () => {
    const subtotal = negotiationItems.reduce((total, item) => {
      let itemSubtotal = 0;

      // TIPO 3: Serviço recorrente (service_days existe)
      // Usar breakdown calculado pelo backend com sobretaxas
      const breakdown = recurrenceBreakdowns[item.id];
      if (item.service_days && item.service_days.length > 0 && breakdown?.total_price) {
        // CORREÇÃO: Usar breakdown.total_price que já tem o cálculo correto
        // de dias normais + dias especiais com sobretaxas
        itemSubtotal = breakdown.total_price * item.current_quantity;
      }
      // TIPO 2: Veículo de frota
      else if (item.is_vehicle && item.fleet_calculated_price) {
        itemSubtotal = item.current_quantity * item.fleet_calculated_price;
      }
      // TIPO 1: Equipamentos com aluguel (pricing_period)
      else {
        // Equipamentos normais: usar cálculo proporcional
        const priceForPeriod = calculateProportionalPrice(
          item.current_price,
          item.pricing_period,
          rentalDays || 1
        );
        itemSubtotal = item.current_quantity * priceForPeriod;
      }

      // Adicionar custo de entrega se existir
      const deliveryCost = item.calculated_total_delivery ? Number(item.calculated_total_delivery) : 0;

      return total + itemSubtotal + deliveryCost;
    }, 0);
    return subtotal * (1 - discountPercentage / 100);
  };

  const saveNegotiation = async () => {
    try {
      setSaving(true);

      if (!proposal) {
        setError('Proposta não encontrada');
        return;
      }

      // VALIDAÇÃO: Verificar se tem datas configuradas (globais OU individuais)
      const hasGlobalDates = startDate && endDate && rentalDays && rentalDays > 0;
      const itemsWithoutDates = negotiationItems.filter(item =>
        !item.individual_start_date || !item.individual_end_date
      );

      // Se não tem datas globais E existem itens sem datas individuais, bloquear
      if (!hasGlobalDates && itemsWithoutDates.length > 0) {
        alert('⚠️ ATENÇÃO: Configure as datas antes de salvar!\n\n' +
          'Você precisa definir:\n' +
          '• Datas globais da proposta (Início e Fim), OU\n' +
          '• Datas individuais para cada item na seção "📦 Logística Individual"\n\n' +
          `${itemsWithoutDates.length} item(ns) sem datas configuradas.`
        );
        setSaving(false);
        return;
      }

      // Preparar dados para salvamento - apenas campos de negociação
      const updatedData: any = {
        // Campos específicos de negociação
        discount_percentage: discountPercentage,
        contractor_obligations: formData.contractor_obligations || [],
        client_obligations: formData.client_obligations || [],
        general_observations: formData.general_observations || [],
        status: proposalStatus,
        internal_notes: proposal.internal_notes || '',
        client_notes: proposal.client_notes || '',
        // Incluir datas editadas
        start_date: startDate,
        end_date: endDate
      };

      // Add modified items to the update
      if (negotiationItems.length > 0) {
        updatedData.items = negotiationItems.map(item => {
          // CORREÇÃO: Sempre usar current_quantity (mesmo para recorrentes)
          // - Para recorrência: quantity = número de locais (ex: banheiros)
          // - O backend multiplica: quantity × total_days calculados
          const quantityToSave = item.current_quantity;

          console.log('📅 Item sendo salvo:', {
            service_name: item.service_name,
            service_days: item.service_days,
            current_quantity: item.current_quantity,
            quantity: quantityToSave
          });

          return {
            service: item.service_id,
            quantity: quantityToSave,
            // Para veículos E recorrentes: usar original_price
            // Para outros: usar current_price (pode ter sido ajustado na negociação)
            unit_price: (item.is_vehicle || item.service_days) ? item.original_price : item.current_price,
            pricing_period: item.pricing_period || 'daily',
            notes: item.notes || '',

            // Campos de configuração de frota (apenas para veículos)
            ...(item.is_vehicle ? {
              fleet_km_included: item.fleet_km_included,
              fleet_hours_included: item.fleet_hours_included,
              fleet_driver_included: item.fleet_driver_included || false,
              fleet_helper_included: item.fleet_helper_included || false,
              fleet_calculated_price: item.current_price,  // Usar current_price aqui (total calculado)
              fleet_config_notes: item.fleet_config_notes
            } : {}),

            // Logística individual por item
            individual_start_date: item.individual_start_date || null,
            individual_end_date: item.individual_end_date || null,
            custom_delivery_address: item.custom_delivery_address || '',
            individual_zip_code: item.individual_zip_code || null,
            individual_street: item.individual_street || null,
            individual_number: item.individual_number || null,
            individual_complement: item.individual_complement || null,
            individual_neighborhood: item.individual_neighborhood || null,
            individual_city: item.individual_city || null,
            individual_state: item.individual_state || null,
            individual_latitude: item.individual_latitude || null,
            individual_longitude: item.individual_longitude || null,

            // Campos calculados de distância - IMPORTANTE: Arredondar para 2 casas decimais
            calculated_distance_km: item.calculated_distance_km
              ? parseFloat(Number(item.calculated_distance_km).toFixed(2))
              : null,
            calculated_duration_minutes: item.calculated_duration_minutes || null,
            route_polyline: item.route_polyline || '',
            distance_calculated_at: item.distance_calculated_at || null,

            // NOVO: Campos de veículo e custos logísticos
            selected_vehicle: item.selected_vehicle || null,
            is_round_trip: item.is_round_trip || false,
            // Arredondar para 2 casas decimais antes de enviar
            calculated_logistics_cost: item.calculated_logistics_cost
              ? parseFloat(Number(item.calculated_logistics_cost).toFixed(2))
              : null,
            calculated_toll_cost: item.calculated_toll_cost
              ? parseFloat(Number(item.calculated_toll_cost).toFixed(2))
              : null,
            calculated_total_delivery: item.calculated_total_delivery
              ? parseFloat(Number(item.calculated_total_delivery).toFixed(2))
              : null,
            toll_details: item.toll_details || null,

            // Recorrência
            service_days: item.service_days || null
          };
        });
      }
      
      // **INTERCEPTOR**: Se mudou para 'aprovada', mostrar modal de escolha de seleção
      if (proposalStatus === 'aprovada' && proposal.status !== 'aprovada') {
        console.log('🎯 Status mudou para aprovada - interceptando para seleção de itens');
        console.log('📊 Status atual da proposta:', proposal.status);
        console.log('📊 Novo status:', proposalStatus);
        console.log('📊 Dados que serão enviados:', updatedData);
        setPendingApprovalData(updatedData);
        setShowItemSelectionChoice(true);
        setSaving(false);
        return;
      }
      
      // 🔍 LOGS DETALHADOS PARA DEBUG
      console.log('🚀 INICIANDO SALVAMENTO DE NEGOCIAÇÃO');
      console.log('📋 Proposta ID:', proposalId);
      console.log('📊 Dados que serão enviados:', JSON.stringify(updatedData, null, 2));
      console.log('🔗 URL do endpoint:', `PATCH /api/v1/proposals/${proposalId}/negotiate/`);

      // Salvar usando o endpoint específico para negociação
      const result = await proposalsApi.negotiateProposal(proposalId!, updatedData);
      
      console.log('✅ Resposta da negociação salva:', result);
      
      // Recarregar dados da proposta para mostrar as mudanças
      await loadProposal();
      
      alert('Negociação salva com sucesso!');
      
    } catch (err: any) {
      console.error('❌ ERRO COMPLETO AO SALVAR NEGOCIAÇÃO:');
      console.error('📄 Erro bruto:', err);
      console.error('🔢 Status Code:', err.response?.status);
      console.error('📊 Dados do erro:', err.response?.data);
      console.error('🌐 URL:', err.config?.url);
      console.error('📝 Método:', err.config?.method);
      console.error('📦 Dados enviados:', err.config?.data);
      console.error('📋 Headers:', err.config?.headers);

      setError(err.response?.data?.detail || err.message || 'Erro ao salvar negociação');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const filteredServices = availableServices
    .filter(service => service.name.toLowerCase().includes(searchService.toLowerCase()))
    .sort((a, b) => {
      // Ordenar por disponibilidade: disponíveis primeiro, indisponíveis por último
      const aAvailable = a.available_quantity > 0;
      const bAvailable = b.available_quantity > 0;

      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      // Se ambos têm mesma disponibilidade, ordenar por nome
      return a.name.localeCompare(b.name);
    });

  // Handlers para modais de seleção de itens
  const handleChoiceModalClose = () => {
    setShowItemSelectionChoice(false);
    setPendingApprovalData(null);
    // Reverter status se o usuário cancelou
    if (proposal) {
      setProposalStatus(proposal.status);
    }
  };

  const handleChooseAutomatic = async () => {
    console.log('🤖 Usuário escolheu seleção automática');
    setShowItemSelectionChoice(false);
    
    try {
      setSaving(true);
      
      console.log('🔄 Tentando salvar proposta com dados:', pendingApprovalData);
      console.log('🔄 ID da proposta:', proposalId);
      
      // Salvar a proposta primeiro
      const result = await proposalsApi.negotiateProposal(proposalId!, pendingApprovalData);
      console.log('✅ Proposta aprovada:', result);
      
      // Recarregar dados para ter a proposta atualizada
      await loadProposal();
      
      // Processar seleção automática para todos os itens que precisam
      const itemsNeedingSelection = result.items?.filter(
        (item: any) => item.service?.track_individual_items && item.quantity > 0
      ) || [];
      
      console.log(`🔍 ${itemsNeedingSelection.length} itens precisam de seleção automática`);
      
      if (itemsNeedingSelection.length > 0) {
        let successCount = 0;
        let errorCount = 0;
        
        for (const item of itemsNeedingSelection) {
          try {
            console.log(`🤖 Processando seleção automática para item ${item.service_name}...`);
            const action = { action: 'auto_select' as const };
            const response = await proposalsApi.selectItems(item.id, action);
            console.log(`✅ Seleção automática concluída para ${item.service_name}: ${response.message}`);
            successCount++;
          } catch (itemErr: any) {
            console.error(`❌ Erro na seleção automática para ${item.service_name}:`, itemErr);
            errorCount++;
          }
        }
        
        // Recarregar dados finais após todas as seleções
        await loadProposal();
        
        if (errorCount === 0) {
          alert(`✅ Proposta aprovada! Seleção automática concluída para ${successCount} item(ns).`);
        } else {
          alert(`⚠️ Proposta aprovada! Seleção automática: ${successCount} sucesso(s), ${errorCount} erro(s). Verifique os itens pendentes.`);
        }
      } else {
        alert('✅ Proposta aprovada! Nenhum item requer seleção automática.');
      }
      
    } catch (err: any) {
      console.error('❌ Erro ao aprovar proposta (AUTOMÁTICO):', err);
      console.error('❌ Erro detalhado:', err.response?.data);
      console.error('❌ Status do erro:', err.response?.status);
      
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          JSON.stringify(err.response?.data) || 
                          err.message || 
                          'Erro ao aprovar proposta';
      
      setError(errorMessage);
      // Reverter status em caso de erro
      if (proposal) {
        setProposalStatus(proposal.status);
      }
    } finally {
      setSaving(false);
      setPendingApprovalData(null);
    }
  };

  const saveAsNewVersion = async () => {
    if (!proposal) {
      setError('Proposta não encontrada');
      return;
    }

    const confirmMessage = `Tem certeza que deseja criar uma nova versão desta proposta?\n\nA proposta atual (${proposal.number}) será mantida como histórico e uma nova versão será criada.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response: any = await proposalsApi.createVersion(proposal.id, {});

      const newProposalId = response.new_proposal_id || response.proposal?.id || response.id;
      const newProposalNumber = response.proposal?.number || response.number;

      alert(`✅ Nova versão criada com sucesso!\n\nNúmero: ${newProposalNumber}\nVocê será redirecionado para a nova versão.`);

      // Redirecionar para negociação da nova versão
      navigate(`/proposals/negotiate?id=${newProposalId}`);

    } catch (err: any) {
      console.error('Erro ao criar nova versão:', err);
      const errorMessage = err.response?.data?.error ||
                          err.response?.data?.message ||
                          'Erro ao criar nova versão da proposta';
      setError(errorMessage);
      alert(`❌ ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChooseManual = async () => {
    console.log('👤 Usuário escolheu seleção manual');
    setShowItemSelectionChoice(false);
    
    try {
      setSaving(true);
      
      // Salvar a proposta primeiro
      const result = await proposalsApi.negotiateProposal(proposalId!, pendingApprovalData);
      console.log('✅ Proposta aprovada:', result);
      
      // Recarregar dados para ter a proposta atualizada
      await loadProposal();
      
      // Encontrar o primeiro item que precisa de seleção manual
      const firstItemNeedingSelection = result.items?.find(
        (item: any) => item.service?.track_individual_items && item.quantity > 0
      );
      
      if (firstItemNeedingSelection) {
        setSelectedProposalItem(firstItemNeedingSelection);
        setIsItemSelectionModalOpen(true);
      } else {
        alert('Proposta aprovada! Nenhum item requer seleção manual.');
      }
      
    } catch (err: any) {
      console.error('Erro ao aprovar proposta:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao aprovar proposta');
      // Reverter status em caso de erro
      if (proposal) {
        setProposalStatus(proposal.status);
      }
    } finally {
      setSaving(false);
      setPendingApprovalData(null);
    }
  };

  const handleCloseItemSelection = () => {
    setIsItemSelectionModalOpen(false);
    setSelectedProposalItem(null);
    // Recarregar dados após seleção
    loadProposal();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4">Carregando negociação...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <h3 className="font-bold">Erro</h3>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/proposals')}
            className="mt-2 bg-red-600 text-white px-3 py-1.5 rounded"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Compacto - 2 Linhas */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-2 rounded-lg mb-3 shadow-lg">
        {/* Linha 1: Info Principal */}
        <div className="flex items-center gap-3 text-xs mb-2 flex-wrap">
          {/* Número da Proposta */}
          <div className="font-bold text-sm">
            {proposal?.number} v{proposal?.version}
          </div>

          <span className="text-blue-200">•</span>

          {/* Cliente Truncado com Tooltip */}
          <div
            className="font-medium cursor-help"
            title={proposal?.client_name || ''}
          >
            {proposal?.client_name && proposal.client_name.length > 20
              ? `${proposal.client_name.substring(0, 20)}...`
              : proposal?.client_name}
          </div>

          <span className="text-blue-200">•</span>

          {/* Responsável */}
          <div className="opacity-90">
            {proposal?.responsible_name}
          </div>

          <span className="text-blue-200">•</span>

          {/* Status com Badge Colorido */}
          <select
            value={proposalStatus}
            onChange={(e) => setProposalStatus(e.target.value as ProposalStatus)}
            className={`
              px-2 py-1 rounded-full text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-white cursor-pointer
              ${proposalStatus === 'aprovada' ? 'bg-green-500 text-white' : ''}
              ${proposalStatus === 'em_negociacao' ? 'bg-yellow-500 text-gray-900' : ''}
              ${proposalStatus === 'em_analise_cliente' ? 'bg-blue-400 text-white' : ''}
              ${proposalStatus === 'novo_lead' ? 'bg-purple-500 text-white' : ''}
              ${proposalStatus === 'cancelada' ? 'bg-red-500 text-white' : ''}
            `}
          >
            <option value="novo_lead">Novo Lead</option>
            <option value="em_negociacao">Em Negociação</option>
            <option value="aprovada">Aprovada</option>
            <option value="em_analise_cliente">Em Análise Cliente</option>
            <option value="cancelada">Cancelada</option>
          </select>

          {/* Botão Registrar Interação */}
          <button
            onClick={() => setShowInteractionModal(true)}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-2 py-1 rounded text-xs inline-flex items-center gap-1"
            title="Registrar Interação"
          >
            <span className="text-sm">📝</span>
            <span>Interação</span>
          </button>

          {/* Botão Fechar (canto direito) */}
          <button
            onClick={() => navigate('/proposals')}
            className="ml-auto bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-2 py-1 rounded text-xs"
            title="Voltar"
          >
            ✕
          </button>
        </div>

        {/* Linha 2: Datas e Ações */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Datas Compactas */}
          <div className="flex items-center gap-2 text-xs">
            <span className="opacity-75">📅</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange(e.target.value, endDate)}
              min={!isHistoricalProposal ? getTodayDate() : undefined}
              className="px-1.5 py-0.5 text-xs text-gray-900 border-0 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 w-28"
            />
            <span className="opacity-75">→</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange(startDate, e.target.value)}
              min={!isHistoricalProposal ? getTodayDate() : undefined}
              className="px-1.5 py-0.5 text-xs text-gray-900 border-0 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 w-28"
            />
            <div className="bg-blue-900 bg-opacity-50 px-2 py-0.5 rounded font-semibold">
              {rentalDays}d
            </div>
          </div>

          {/* Botões de Ação Compactos */}
          <div className="flex gap-1.5 items-center">
            <button
              onClick={() => setShowHistoryModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs whitespace-nowrap inline-flex items-center gap-1"
              title="Ver Histórico da Negociação"
            >
              <span className="text-sm">💬</span>
              <span>Histórico</span>
              {negotiationHistory.length > 0 && (
                <span className="bg-red-500 text-white px-1 rounded-full text-[9px] font-bold leading-none">
                  {negotiationHistory.length}
                </span>
              )}
            </button>
            <button
              onClick={saveAsNewVersion}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1"
              title="Salvar como Nova Versão"
            >
              <span className="text-sm">📋</span>
              <span>{saving ? '...' : 'Nova Versão'}</span>
            </button>
            <button
              onClick={saveNegotiation}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs font-medium disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1"
              title="Salvar Negociação"
            >
              <span className="text-sm">💾</span>
              <span>{saving ? 'Salvando...' : 'Salvar'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Resumo Financeiro Compacto - 1 Linha */}
      <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-200 mb-3">
        <div className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-600">💰 Total:</span>
            <span className="font-bold text-green-600">{formatCurrency(calculateCurrentTotal())}</span>
            <span className="text-gray-400">|</span>
            <label className="text-gray-600">Desconto:</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(parseFloat(e.target.value) || 0)}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <span className="text-gray-600">%</span>
            <span className="text-gray-400">→</span>
            <span className="font-bold text-blue-600">
              {formatCurrency(calculateCurrentTotal() * (1 - discountPercentage / 100))}
            </span>
          </div>
          <button
            onClick={() => setShowAddService(true)}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center whitespace-nowrap"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Adicionar Equipamento
          </button>
        </div>
      </div>

      {/* Itens da Negociação - Largura Total */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="space-y-3">
          {negotiationItems.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-sm text-gray-900">{item.service_name}</h4>
                      {item.original_quantity === 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Novo Item
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Linha Compacta de Quantidades e Preços */}
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    {/* Qtd */}
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-600">Qtd:</span>
                      <input
                        type="number"
                        min="1"
                        value={item.current_quantity}
                        onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        title="Quantidade de locais/unidades (ex: número de banheiros)"
                      />
                    </div>

                    <span className="text-gray-300">|</span>

                    {/* Preço Unitário */}
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-600">Preço Unit.:</span>
                      <span className="text-xs bg-gray-50 px-2 py-1 rounded">
                        {formatCurrency(item.original_price)}
                        {/* Para recorrência, mostrar /limpeza */}
                        {item.service_days && item.service_days.length > 0 ? (
                          <span className="text-xs text-blue-600 ml-0.5">/limpeza</span>
                        ) : item.pricing_period && item.pricing_period !== 'fixed' && (
                          <span className="text-xs text-blue-600 ml-0.5">
                            /{item.pricing_period === 'daily' ? 'dia' : item.pricing_period === 'weekly' ? 'sem' : 'mês'}
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Periodicidade e Cálculo (inline) */}
                    {!item.is_vehicle && item.pricing_period && item.pricing_period !== 'fixed' && (
                      <>
                        <span className="text-gray-300">|</span>

                        {/* Periodicidade */}
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-blue-600">📅</span>
                          <span className="text-xs text-gray-600">
                            {item.pricing_period === 'daily' ? 'Diária' : item.pricing_period === 'weekly' ? 'Semanal' : 'Mensal'}
                          </span>
                          <span className="text-xs text-gray-400">(auto)</span>
                        </div>

                        <span className="text-gray-300">|</span>

                        {/* Cálculo */}
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-blue-600">💰</span>
                          <span className="text-xs text-gray-700">
                            {(() => {
                              const days = rentalDays || 1;
                              const price = item.current_price;
                              const qty = item.current_quantity;
                              const period = item.pricing_period;

                              if (period === 'daily') {
                                return `${qty} × R$ ${price.toFixed(2)}/dia × ${days} dias`;
                              } else if (period === 'weekly') {
                                const weeks = Math.floor(days / 7);
                                const remainingDays = days % 7;
                                if (remainingDays > 0) {
                                  return `${qty} × R$ ${price.toFixed(2)}/sem × ${weeks} sem + ${remainingDays}d`;
                                }
                                return `${qty} × R$ ${price.toFixed(2)}/sem × ${weeks} sem`;
                              } else {
                                const months = Math.floor(days / 30);
                                const remainingDays = days % 30;
                                if (remainingDays > 0) {
                                  return `${qty} × R$ ${price.toFixed(2)}/mês × ${months} meses + ${remainingDays}d`;
                                }
                                return `${qty} × R$ ${price.toFixed(2)}/mês × ${months} meses`;
                              }
                            })()}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Configuração de Frota (apenas para veículos) */}
                  {item.is_vehicle && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <h5 className="text-xs font-semibold text-blue-800 mb-2">⚙️ Configuração de Frota</h5>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            KM Incluídos/dia
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={item.fleet_km_included || ''}
                            onChange={(e) => updateFleetConfig(item.id, 'fleet_km_included', parseInt(e.target.value) || 0)}
                            placeholder={item.default_km_included?.toString() || '0'}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <div className="text-xs text-gray-500 mt-0.5">
                            Padrão: {item.default_km_included || 0} km
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Horas Incluídas/dia
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={item.fleet_hours_included || ''}
                            onChange={(e) => updateFleetConfig(item.id, 'fleet_hours_included', parseInt(e.target.value) || 0)}
                            placeholder={item.default_hours_included?.toString() || '0'}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <div className="text-xs text-gray-500 mt-0.5">
                            Padrão: {item.default_hours_included || 0} h
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Motorista
                          </label>
                          <select
                            value={item.fleet_driver_included ? 'yes' : 'no'}
                            onChange={(e) => updateFleetConfig(item.id, 'fleet_driver_included', e.target.value === 'yes')}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="no">Cliente fornece</option>
                            <option value="yes">Incluir motorista</option>
                          </select>
                          {item.price_driver_daily && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              R$ {item.price_driver_daily.toFixed(2)}/dia
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Ajudante
                          </label>
                          <select
                            value={item.fleet_helper_included ? 'yes' : 'no'}
                            onChange={(e) => updateFleetConfig(item.id, 'fleet_helper_included', e.target.value === 'yes')}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="no">Cliente fornece</option>
                            <option value="yes">Incluir ajudante</option>
                          </select>
                          {item.price_helper_daily && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              R$ {item.price_helper_daily.toFixed(2)}/dia
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Preço Calculado e Notas */}
                      {item.fleet_calculated_price && (
                        <div className="mt-2 p-2 bg-white border border-blue-300 rounded">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-medium text-blue-800">💰 Preço Calculado:</span>
                            <span className="text-sm font-bold text-blue-900">
                              {formatCurrency(item.fleet_calculated_price)}
                            </span>
                          </div>
                          {item.fleet_config_notes && (
                            <details className="mt-1">
                              <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                                Ver detalhes do cálculo
                              </summary>
                              <pre className="text-xs text-gray-700 mt-1 whitespace-pre-wrap bg-gray-50 p-2 rounded">
                                {item.fleet_config_notes}
                              </pre>
                            </details>
                          )}
                          {item.current_price !== item.fleet_calculated_price && (
                            <div className="mt-1 text-xs text-orange-600 font-medium">
                              ⚠️ Preço ajustado manualmente para {formatCurrency(item.current_price)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Logística Individual por Item */}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium text-secondary-900 hover:text-primary-600 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      📦 Logística Individual (opcional)
                    </summary>
                    <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">
                      {/* Datas inline */}
                      <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                        <span className="text-gray-600">📅</span>
                        <input
                          type="date"
                          value={item.individual_start_date || ''}
                          onChange={(e) => updateItemDates(item.id, e.target.value || undefined, item.individual_end_date)}
                          min={!isHistoricalProposal ? getTodayDate() : undefined}
                          placeholder="Início"
                          className="px-2 py-0.5 border border-gray-300 rounded text-xs w-32"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                          type="date"
                          value={item.individual_end_date || ''}
                          onChange={(e) => updateItemDates(item.id, item.individual_start_date, e.target.value || undefined)}
                          min={!isHistoricalProposal ? getTodayDate() : undefined}
                          placeholder="Fim"
                          className="px-2 py-0.5 border border-gray-300 rounded text-xs w-32"
                        />
                      </div>

                      {/* Endereço ultra compacto - 2 linhas */}
                      <div className="space-y-1 border-t pt-1.5">
                        {/* Linha 1: CEP + Rua + Nº */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-600">CEP:</span>
                          <input
                            type="text"
                            value={item.individual_zip_code || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_zip_code', e.target.value)}
                            placeholder="00000-000"
                            maxLength={9}
                            className="w-24 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                          />
                          <button
                            type="button"
                            onClick={() => item.individual_zip_code && fetchAddressByCep(item.id, item.individual_zip_code)}
                            disabled={!item.individual_zip_code || loadingCep === item.id}
                            className="px-1.5 py-0.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:bg-gray-400"
                          >
                            {loadingCep === item.id ? '⏳' : '🔍'}
                          </button>
                          <span className="text-xs text-gray-600">Rua:</span>
                          <input
                            type="text"
                            value={item.individual_street || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_street', e.target.value)}
                            placeholder="Nome da rua"
                            className="flex-1 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                          />
                          <span className="text-xs text-gray-600">Nº:</span>
                          <input
                            type="text"
                            value={item.individual_number || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_number', e.target.value)}
                            placeholder="Nº"
                            className="w-14 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                          />
                        </div>

                        {/* Linha 2: Comp + Bairro + Cidade + UF */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-600">Comp:</span>
                          <input
                            type="text"
                            value={item.individual_complement || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_complement', e.target.value)}
                            placeholder="Apto"
                            className="w-20 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                          />
                          <span className="text-xs text-gray-600">Bairro:</span>
                          <input
                            type="text"
                            value={item.individual_neighborhood || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_neighborhood', e.target.value)}
                            placeholder="Bairro"
                            className="flex-1 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                          />
                          <span className="text-xs text-gray-600">Cidade:</span>
                          <input
                            type="text"
                            value={item.individual_city || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_city', e.target.value)}
                            placeholder="Cidade"
                            className="flex-1 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                          />
                          <span className="text-xs text-gray-600">UF:</span>
                          <input
                            type="text"
                            value={item.individual_state || ''}
                            onChange={(e) => updateIndividualAddressField(item.id, 'individual_state', e.target.value.toUpperCase())}
                            placeholder="SP"
                            maxLength={2}
                            className="w-10 px-1.5 py-0.5 border border-gray-300 rounded text-xs uppercase"
                          />
                        </div>

                        {/* Botão compacto para atualizar coordenadas */}
                        {item.individual_city && item.individual_state && (
                          <button
                            onClick={() => updateCoordinatesFromAddress(item.id)}
                            disabled={updatingCoordinates === item.id}
                            className="w-full px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-400"
                            title="Atualizar coordenadas do endereço"
                          >
                            {updatingCoordinates === item.id ? '⏳ Atualizando...' : '🗺️ Atualizar Coordenadas'}
                          </button>
                        )}

                        {/* Coordenadas - tudo em 1 linha */}
                        <div className="mt-2 pt-1.5 border-t border-gray-300">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-600">🌍 URL:</span>
                            <input
                              type="text"
                              placeholder="Google Maps URL"
                              onPaste={(e) => {
                                const url = e.clipboardData.getData('text');
                                if (url) setTimeout(() => extractCoordinatesFromGoogleMapsUrl(item.id, url), 100);
                              }}
                              className="flex-1 px-1.5 py-0.5 border border-purple-300 rounded text-xs"
                            />
                            <button
                              onClick={(e) => {
                                const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                                if (input?.value) extractCoordinatesFromGoogleMapsUrl(item.id, input.value);
                              }}
                              disabled={extractingFromUrl === item.id}
                              className="px-1.5 py-0.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:bg-gray-400"
                            >
                              {extractingFromUrl === item.id ? '⏳' : '📍'}
                            </button>
                            <span className="text-xs text-gray-600">Lat:</span>
                            <input
                              type="number"
                              step="0.000001"
                              value={item.individual_latitude || ''}
                              onChange={(e) => updateIndividualAddressField(item.id, 'individual_latitude', e.target.value ? parseFloat(e.target.value) : undefined)}
                              placeholder="-23.55"
                              className="w-24 px-1.5 py-0.5 border border-gray-300 rounded text-xs font-mono"
                            />
                            <span className="text-xs text-gray-600">Long:</span>
                            <input
                              type="number"
                              step="0.000001"
                              value={item.individual_longitude || ''}
                              onChange={(e) => updateIndividualAddressField(item.id, 'individual_longitude', e.target.value ? parseFloat(e.target.value) : undefined)}
                              placeholder="-46.63"
                              className="w-24 px-1.5 py-0.5 border border-gray-300 rounded text-xs font-mono"
                            />
                          </div>
                        </div>

                        {/* Seção de Veículo e Cálculo compacta */}
                        {((item.individual_latitude && item.individual_longitude) || (item.individual_city && item.individual_state)) && (
                          <div className="mt-2 pt-1.5 border-t border-gray-300 space-y-1.5">
                            <span className="text-xs text-gray-700 font-medium">🚛 Logística</span>

                            {/* Veículo compacto */}
                            <select
                              value={item.selected_vehicle || ''}
                              onChange={(e) => updateIndividualAddressField(item.id, 'selected_vehicle', e.target.value || undefined)}
                              className="w-full px-2 py-0.5 border border-gray-300 rounded text-xs"
                            >
                              <option value="">Selecione veículo (opcional)</option>
                              {vehicles.map(vehicle => (
                                <option key={vehicle.id} value={vehicle.id}>
                                  {vehicle.name} - R$ {Number(vehicle.price_per_km || 0).toFixed(2)}/km
                                </option>
                              ))}
                            </select>

                            {/* Checkbox compacto + Botão calcular */}
                            <div className="flex gap-2">
                              {item.selected_vehicle && (
                                <label className="flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={item.is_round_trip || false}
                                    onChange={(e) => updateIndividualAddressField(item.id, 'is_round_trip', e.target.checked)}
                                    className="w-3 h-3"
                                  />
                                  <span>🔄 Ida/Volta</span>
                                </label>
                              )}
                              <button
                                onClick={() => calculateDistance(item.id)}
                                disabled={calculatingDistance === item.id}
                                className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded disabled:bg-gray-400"
                              >
                                {calculatingDistance === item.id ? '⏳ Calculando...' : '🚗 Calcular Distância'}
                              </button>
                            </div>

                            {/* Resultado compacto */}
                            {item.calculated_distance_km && (
                              <div className="p-2 bg-green-50 border border-green-300 rounded space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="text-green-800">📏 Distância:</span>
                                  <span className="font-semibold">{Number(item.calculated_distance_km).toFixed(2)} km</span>
                                </div>
                                {item.calculated_duration_minutes && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-green-800">⏱️ Tempo:</span>
                                    <span>{Number(item.calculated_duration_minutes) >= 60
                                      ? `${Math.floor(Number(item.calculated_duration_minutes) / 60)}h ${Number(item.calculated_duration_minutes) % 60}min`
                                      : `${Number(item.calculated_duration_minutes)}min`}</span>
                                  </div>
                                )}

                                {/* Custos */}
                                {item.calculated_total_delivery && (
                                  <div className="pt-1 mt-1 border-t border-green-300 space-y-1">
                                    <div className="flex justify-between text-xs">
                                      <span className="text-green-800">⛽ Rodagem:</span>
                                      <span>R$ {Number(item.calculated_logistics_cost).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                      <span className="text-green-800">🎫 Pedágios:</span>
                                      <span>R$ {Number(item.calculated_toll_cost).toFixed(2)}</span>
                                    </div>
                                    {item.toll_details?.tolls && item.toll_details.tolls.length > 0 && (
                                      <details className="ml-2">
                                        <summary className="text-[10px] text-green-700 cursor-pointer">Ver pedágios</summary>
                                        <div className="ml-2 mt-0.5 space-y-0.5">
                                          {item.toll_details.tolls.map((toll: any, idx: number) => (
                                            <p key={idx} className="text-[9px] text-green-700">• {toll.name}: R$ {toll.cost.toFixed(2)}</p>
                                          ))}
                                        </div>
                                      </details>
                                    )}
                                    <div className="flex justify-between text-xs pt-1 border-t border-green-400 font-bold">
                                      <span className="text-green-900">💵 TOTAL:</span>
                                      <span className="text-green-900">R$ {Number(item.calculated_total_delivery).toFixed(2)}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Indicadores compactos */}
                      {(item.individual_start_date || item.individual_end_date || item.individual_zip_code || (item.individual_latitude && item.individual_longitude)) && (
                        <div className="mt-2 flex flex-wrap gap-1 text-xs">
                          {(item.individual_start_date || item.individual_end_date) && (
                            <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-blue-800">
                              ✓ Datas individuais
                            </span>
                          )}
                          {(item.individual_latitude && item.individual_longitude) && (
                            <span className="px-2 py-0.5 bg-green-50 border border-green-200 rounded text-green-800">
                              ✓ Coordenadas
                            </span>
                          )}
                          {(item.individual_zip_code || item.individual_city) && !(item.individual_latitude && item.individual_longitude) && (
                            <span className="px-2 py-0.5 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                              ⚠️ Sem coordenadas
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </details>

                  {/* Recorrência - Apenas para serviços com preço fixo */}
                  {item.service.item_type === 'service' && item.service.pricing_model === 'fixed' && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs font-medium text-secondary-900 hover:text-primary-600 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        📅 Recorrência (opcional)
                      </summary>
                      <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded">
                        <p className="text-xs text-purple-700 mb-3">
                          Configure os dias da semana em que este serviço será executado. O sistema calculará automaticamente as ocorrências no período.
                        </p>

                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: 0, label: 'Seg', full: 'Segunda' },
                              { value: 1, label: 'Ter', full: 'Terça' },
                              { value: 2, label: 'Qua', full: 'Quarta' },
                              { value: 3, label: 'Qui', full: 'Quinta' },
                              { value: 4, label: 'Sex', full: 'Sexta' },
                              { value: 5, label: 'Sáb', full: 'Sábado' },
                              { value: 6, label: 'Dom', full: 'Domingo' }
                            ].map(day => {
                              const isSelected = item.service_days?.includes(day.value) || false;
                              return (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => {
                                    const currentDays = item.service_days || [];
                                    const newDays = isSelected
                                      ? currentDays.filter(d => d !== day.value)
                                      : [...currentDays, day.value].sort();
                                    updateItemRecurrence(item.id, newDays.length > 0 ? newDays : undefined);
                                  }}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    isSelected
                                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                                      : 'bg-white text-purple-600 border border-purple-300 hover:bg-purple-100'
                                  }`}
                                  title={day.full}
                                >
                                  {day.label}
                                </button>
                              );
                            })}
                          </div>

                          {item.service_days && item.service_days.length > 0 && (
                            <div className="space-y-2">
                              <div className="p-2 bg-purple-100 border border-purple-300 rounded">
                                <p className="text-xs text-purple-900 font-medium">
                                  📊 Resumo da Recorrência
                                </p>
                                <p className="text-xs text-purple-800 mt-1">
                                  Dias selecionados: {item.service_days.map(d =>
                                    ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'][d]
                                  ).join(', ')}
                                </p>
                                {(() => {
                                  const effectiveStart = item.individual_start_date || startDate;
                                  const effectiveEnd = item.individual_end_date || endDate;
                                  const breakdown = recurrenceBreakdowns[item.id];

                                  if (effectiveStart && effectiveEnd) {
                                    const occurrences = breakdown ? breakdown.total_occurrences : calculateOccurrences(effectiveStart, effectiveEnd, item.service_days);

                                    return (
                                      <div className="mt-1">
                                        <p className="text-xs text-purple-800">
                                          <strong>{occurrences} ocorrências</strong> no período de {effectiveStart} a {effectiveEnd}
                                        </p>

                                        {/* Mostrar breakdown detalhado dentro do resumo */}
                                        {breakdown && (
                                          <div className="mt-2 space-y-1 text-xs">
                                            {/* Total de dias no período */}
                                            <div className="flex justify-between text-purple-900 font-semibold mb-1">
                                              <span>📆 Total de dias no período:</span>
                                              <span>{breakdown.total_occurrences} dias</span>
                                            </div>

                                            {/* Dias normais (seg-sex) */}
                                            <div className="flex justify-between text-purple-700">
                                              <span>🔹 Dias úteis (Seg-Sex):</span>
                                              <span className="font-medium">{breakdown.normal_days_count} dias</span>
                                            </div>

                                            {breakdown.has_surcharge && breakdown.special_days_count > 0 && (() => {
                                              const saturdays = breakdown.details.filter((d: any) => d.weekday === 'Sab').length;
                                              const sundays = breakdown.details.filter((d: any) => d.weekday === 'Dom').length;
                                              // Feriados APENAS em dias úteis (excluir sábados e domingos)
                                              const holidaysOnWeekdays = breakdown.details.filter((d: any) =>
                                                d.is_special &&
                                                d.reason &&
                                                d.reason !== 'weekend' &&
                                                d.weekday !== 'Sab' &&
                                                d.weekday !== 'Dom'
                                              ).length;

                                              return (
                                                <>
                                                  {saturdays > 0 && (
                                                    <div className="flex justify-between text-purple-700">
                                                      <span>🔸 Sábados:</span>
                                                      <span className="font-medium">{saturdays} dias</span>
                                                    </div>
                                                  )}
                                                  {sundays > 0 && (
                                                    <div className="flex justify-between text-purple-700">
                                                      <span>🔸 Domingos:</span>
                                                      <span className="font-medium">{sundays} dias</span>
                                                    </div>
                                                  )}
                                                  {holidaysOnWeekdays > 0 && (
                                                    <div className="flex justify-between text-red-700">
                                                      <span>🔴 Feriados em dias úteis:</span>
                                                      <span className="font-medium">{holidaysOnWeekdays} dias</span>
                                                    </div>
                                                  )}
                                                  <div className="flex justify-between text-purple-900 font-semibold mt-1 pt-1 border-t border-purple-200">
                                                    <span>⭐ Total com sobretaxa:</span>
                                                    <span>{breakdown.special_days_count} dias</span>
                                                  </div>
                                                </>
                                              );
                                            })()}

                                            <div className="border-t border-purple-300 my-2"></div>

                                            {/* Cálculo de preços COM QUANTIDADE */}
                                            <div className="space-y-1">
                                              <div className="flex justify-between text-purple-700">
                                                <span>Dias normais ({breakdown.normal_days_count}x):</span>
                                                <span className="font-mono text-xs">
                                                  {item.current_quantity > 1 && `${item.current_quantity} × `}
                                                  R$ {breakdown.normal_price.toFixed(2)} × {breakdown.normal_days_count} = R$ {(breakdown.normal_days_count * breakdown.normal_price * item.current_quantity).toFixed(2)}
                                                </span>
                                              </div>
                                              {breakdown.has_surcharge && breakdown.special_days_count > 0 && (
                                                <div className="flex justify-between text-purple-900 font-medium">
                                                  <span>Dias especiais ({breakdown.special_days_count}x):</span>
                                                  <span className="font-mono text-xs">
                                                    {item.current_quantity > 1 && `${item.current_quantity} × `}
                                                    R$ {breakdown.special_price.toFixed(2)} × {breakdown.special_days_count} = R$ {(breakdown.special_days_count * breakdown.special_price * item.current_quantity).toFixed(2)}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                            <div className="flex justify-between text-green-700 font-bold pt-1 border-t border-purple-300 bg-green-50 px-2 py-1 rounded">
                                              <span>💰 TOTAL ({item.current_quantity} {item.current_quantity > 1 ? 'unidades' : 'unidade'}):</span>
                                              <span className="font-mono">R$ {(breakdown.total_price * item.current_quantity).toFixed(2)}</span>
                                            </div>

                                            {/* Mostrar TODOS os feriados identificados (inclusive os que caem em fim de semana) */}
                                            {breakdown.details && (() => {
                                              // Buscar TODOS os feriados (inclusive os em weekend)
                                              const allHolidays = breakdown.details.filter((d: any) => d.reason && d.reason !== 'weekend');

                                              if (allHolidays.length === 0) return null;

                                              return (
                                                <div className="mt-2 pt-2 border-t border-purple-300">
                                                  <p className="text-xs font-medium text-purple-900 mb-1">
                                                    📅 Feriados no período:
                                                  </p>
                                                  <div className="text-xs text-purple-800 space-y-0.5">
                                                    {allHolidays.slice(0, 5).map((d: any, idx: number) => {
                                                      const isWeekend = d.weekday === 'Sáb' || d.weekday === 'Dom';
                                                      return (
                                                        <div key={idx} className={isWeekend ? 'text-blue-700' : 'text-red-700'}>
                                                          • {d.date} ({d.weekday}) - {d.reason}
                                                          {isWeekend && ' 🔵 (já em fim de semana)'}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              );
                                            })()}

                                            <p className="text-xs text-purple-600 mt-2 italic">
                                              {breakdown.surcharge_details.type === 'percentage'
                                                ? `Acréscimo de ${breakdown.surcharge_details.value}% em dias especiais`
                                                : `Acréscimo de R$ ${breakdown.surcharge_details.value.toFixed(2)} em dias especiais`}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  return (
                                    <p className="text-xs text-orange-600 mt-1">
                                      ⚠️ Configure as datas para ver a quantidade de ocorrências
                                    </p>
                                  );
                                })()}
                              </div>
                            </div>
                          )}

                          {!item.service_days || item.service_days.length === 0 ? (
                            <p className="text-xs text-purple-600 italic">
                              Selecione os dias da semana para ativar a recorrência
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </details>
                  )}

                  {/* RESUMO VISÍVEL DA RECORRÊNCIA (sempre visível, fora do details) */}
                  {item.service_days && item.service_days.length > 0 && (() => {
                    const breakdown = recurrenceBreakdowns[item.id];
                    if (!breakdown) return null;

                    return (
                      <div className="mt-2 p-2 bg-gradient-to-r from-green-50 to-blue-50 border border-green-300 rounded-lg">
                        <div className="flex items-center justify-between mb-1.5">
                          <h4 className="text-xs font-bold text-gray-800">💰 Cálculo Total</h4>
                          <span className="text-xs text-gray-600">{item.current_quantity} {item.current_quantity > 1 ? 'unidades' : 'unidade'}</span>
                        </div>

                        <div className="space-y-0.5">
                          {/* Dias normais */}
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-700">Dias normais ({breakdown.normal_days_count}x):</span>
                            <span className="font-mono text-gray-900 font-medium">
                              {item.current_quantity > 1 && `${item.current_quantity} × `}
                              R$ {breakdown.normal_price.toFixed(2)} × {breakdown.normal_days_count} =
                              <strong className="text-green-700 ml-1">
                                R$ {(breakdown.normal_days_count * breakdown.normal_price * item.current_quantity).toFixed(2)}
                              </strong>
                            </span>
                          </div>

                          {/* Dias especiais */}
                          {breakdown.has_surcharge && breakdown.special_days_count > 0 && (
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-700">Dias especiais ({breakdown.special_days_count}x):</span>
                              <span className="font-mono text-gray-900 font-medium">
                                {item.current_quantity > 1 && `${item.current_quantity} × `}
                                R$ {breakdown.special_price.toFixed(2)} × {breakdown.special_days_count} =
                                <strong className="text-orange-700 ml-1">
                                  R$ {(breakdown.special_days_count * breakdown.special_price * item.current_quantity).toFixed(2)}
                                </strong>
                              </span>
                            </div>
                          )}

                          {/* Total Final */}
                          <div className="flex justify-between items-center pt-1.5 mt-1.5 border-t-2 border-green-400">
                            <span className="text-xs font-bold text-gray-900">TOTAL:</span>
                            <span className="text-sm font-bold text-green-700 font-mono">
                              R$ {(breakdown.total_price * item.current_quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <p className="text-xs text-gray-600 mt-1.5 italic text-center">
                          ℹ️ Expanda "Recorrência" acima para ver detalhes completos
                        </p>
                      </div>
                    );
                  })()}

                  {/* Total do Item */}
                  <div className="mt-2 pt-2 border-t text-sm text-right">
                    <span className="text-gray-700">Subtotal: </span>
                    <span className="font-semibold text-green-600">
                      {(() => {
                        let serviceSubtotal = 0;

                        // TIPO 3: Serviço recorrente (service_days existe)
                        const breakdown = recurrenceBreakdowns[item.id];
                        if (item.service_days && item.service_days.length > 0 && breakdown?.total_price) {
                          // CORREÇÃO: Multiplicar breakdown.total_price pela quantidade
                          // - breakdown.total_price = preço para 1 local/unidade
                          // - current_quantity = número de locais (ex: banheiros)
                          serviceSubtotal = breakdown.total_price * item.current_quantity;
                        }
                        // TIPO 2: Veículo de frota
                        else if (item.is_vehicle && item.fleet_calculated_price) {
                          serviceSubtotal = item.current_quantity * item.fleet_calculated_price;
                        }
                        // TIPO 1: Equipamentos com aluguel (pricing_period)
                        else {
                          serviceSubtotal = item.current_quantity * calculateProportionalPrice(item.current_price, item.pricing_period, rentalDays || 1);
                        }

                        // Adicionar custo de entrega se existir
                        const deliveryCost = item.calculated_total_delivery ? Number(item.calculated_total_delivery) : 0;

                        return formatCurrency(serviceSubtotal + deliveryCost);
                      })()}
                    </span>
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Seção de Obrigações e Observações */}
      <div className="mt-4 bg-white p-3 rounded-lg shadow">
        <ObligationsStep
          formData={formData}
          setFormData={setFormData}
          errors={{}}
        />
      </div>

      {/* Modal de Adicionar Equipamento */}
      {showAddService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-[75%] max-w-[1280px] max-h-[92vh] overflow-hidden">
            <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900">Adicionar Equipamento</h3>
              <button
                onClick={() => setShowAddService(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="px-3 py-2 border-b bg-gray-50">
              <input
                type="text"
                placeholder="Buscar equipamento..."
                value={searchService}
                onChange={(e) => setSearchService(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
              />
            </div>

            <div className="max-h-[calc(92vh-140px)] overflow-y-auto px-3 py-2">
              <div className="space-y-1">
                {filteredServices.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => addService(service)}
                    disabled={service.available_quantity === 0}
                    className={`w-full text-left px-2 py-1.5 border border-gray-200 rounded transition-colors ${
                      service.available_quantity === 0
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 text-xs">
                      <div className="font-semibold text-gray-900 w-48 truncate">
                        {service.name}
                      </div>
                      <div className="text-gray-600 w-32 truncate">
                        {service.category_name}
                      </div>
                      <div className="text-gray-700 font-medium w-28 text-right">
                        {formatCurrency(
                          service.item_type === 'service' && service.base_rate
                            ? service.base_rate
                            : (service.daily_price || service.weekly_price || service.monthly_price || 0)
                        )}
                        {service.item_type === 'service' && service.pricing_model === 'fixed' ? '' : (
                          <>
                            {service.daily_price && '/dia'}
                            {!service.daily_price && service.weekly_price && '/sem'}
                            {!service.daily_price && !service.weekly_price && service.monthly_price && '/mês'}
                          </>
                        )}
                      </div>
                      <div className="text-gray-600 w-36">
                        {service.track_individual_items
                          ? `${service.available_quantity || 0}/${service.total_quantity || 0} disp.`
                          : `Qtd: ${service.total_quantity || 0}`
                        }
                        {service.maintenance_quantity > 0 && (
                          <span className="text-yellow-600 ml-1">
                            ({service.maintenance_quantity} manut.)
                          </span>
                        )}
                      </div>
                      <div className="ml-auto flex-shrink-0">
                        {service.available_quantity === 0 ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Indisponível
                          </span>
                        ) : service.available_quantity && service.available_quantity < 5 ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Baixo estoque
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Disponível
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Alerta - Configurar Datas */}
      {showDatesWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-white mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-xl font-bold text-white">
                  ⚠️ Configure as Datas
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                As datas são necessárias para calcular os valores corretamente.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-2">📋 Você pode:</p>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start">
                    <span className="font-bold mr-2">1.</span>
                    <span>Configure <strong>datas globais</strong> no cabeçalho (Início e Fim)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold mr-2">2.</span>
                    <span>Configure <strong>datas individuais</strong> para cada item na seção "📦 Logística Individual"</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                <p className="text-sm text-amber-900">
                  <strong>💡 Importante:</strong> Sem datas, os cálculos de preço ficarão incorretos e você não poderá salvar a proposta.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  // Salvar no localStorage que o usuário já viu e dismissou o aviso
                  const warningDismissedKey = `dates_warning_dismissed_${proposalId}`;
                  localStorage.setItem(warningDismissedKey, 'true');
                  setShowDatesWarningModal(false);
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Escolha de Seleção de Itens */}
      <ItemSelectionChoiceModal
        isOpen={showItemSelectionChoice}
        onClose={handleChoiceModalClose}
        onChooseAutomatic={handleChooseAutomatic}
        onChooseManual={handleChooseManual}
        proposalNumber={proposal?.number}
        itemsCount={negotiationItems.length}
      />

      {/* Modal de Seleção Manual de Itens */}
      {selectedProposalItem && (
        <ItemSelectionModal
          proposalItem={selectedProposalItem}
          isOpen={isItemSelectionModalOpen}
          onClose={handleCloseItemSelection}
          onSelectionComplete={handleCloseItemSelection}
        />
      )}

      {/* Modal de Histórico da Negociação */}
      {showHistoryModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full"
            style={{ maxWidth: '900px', height: '600px', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do Modal - Fixo */}
            <div
              className="p-4 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-lg"
              style={{ flexShrink: 0 }}
            >
              <h2 className="text-sm font-bold text-gray-900 flex items-center">
                <ClockIcon className="h-4 w-4 mr-1.5 text-purple-600" />
                Histórico da Negociação
                <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {negotiationHistory.length}
                </span>
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
              >
                ×
              </button>
            </div>

            {/* Corpo do Modal - Com Scroll GARANTIDO */}
            <div
              className="p-4"
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0
              }}
            >
              <div className="space-y-3">
                {negotiationHistory.length > 0 ? (
                  negotiationHistory.map((entry) => (
                    <div key={entry.id} className="border-l-4 border-purple-500 pl-4 py-3 bg-gray-50 rounded-r hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <p className="text-xs text-gray-900 leading-relaxed" style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                            {entry.description}
                          </p>
                          <div className="mt-2 text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="flex items-center">
                              <UserIcon className="h-3 w-3 mr-1" />
                              <span className="font-medium">{entry.user_name}</span>
                            </span>
                            <span className="flex items-center">
                              <CalendarIcon className="h-3 w-3 mr-1" />
                              <span>{formatDateTime(entry.timestamp)}</span>
                            </span>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.type === 'interaction' ? 'bg-blue-100 text-blue-800' :
                            entry.type === 'item_change' ? 'bg-yellow-100 text-yellow-800' :
                            entry.type === 'price_change' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                          style={{ flexShrink: 0 }}
                        >
                          {entry.type === 'interaction' ? 'Conversa' :
                           entry.type === 'item_change' ? 'Item' :
                           entry.type === 'price_change' ? 'Preço' :
                           'Status'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <ClockIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">Nenhum histórico de negociação ainda</p>
                    <p className="text-gray-400 text-xs mt-1">As interações aparecerão aqui conforme você registra</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Registrar Interação */}
      {showInteractionModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowInteractionModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do Modal */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Registrar Interação</h3>
              <button
                onClick={() => setShowInteractionModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="p-4">
              <textarea
                value={newInteraction}
                onChange={(e) => setNewInteraction(e.target.value)}
                placeholder="Registre aqui o que aconteceu na negociação com o cliente..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              />
            </div>

            {/* Footer do Modal */}
            <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowInteractionModal(false)}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  addInteraction();
                  setShowInteractionModal(false);
                }}
                disabled={!newInteraction.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                Adicionar ao Histórico
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botões removidos - agora estão no header */}
    </div>
  );
};

export default NegotiateProposal;